import { planFormation, type FormationShape } from '../../shared/game/formation.js';
import type { EnemyDefinition, EnemyState, GameContent, HeroDefinition, HeroState, UnitDefinition, UnitState } from '../../shared/game/world-types.js';
import { createMapVisual } from './environment/map-visual.js';
import { castOnlyIntoShadows } from './environment/scenery-shadows.js';
import { terrainHeight } from '../../shared/game/terrain.js';
import { conformTerrainDecal } from './environment/terrain-decals.js';
import { createUnitVisual } from './entities/unit-visual.js';
import { Torches } from './entities/torches.js';
import { ownFigureMaterials, shareFigureMaterials } from './entities/figure-materials.js';
import { GROUND_UI_LAYER } from './environment/ground-decal.js';
import { buildHeroPlate, updateHeroPlate } from './entities/hero-plate.js';
import { buildEnemyPlate, conditionOf, updateEnemyPlate } from './entities/enemy-plate.js';
import { GAUGE_FRONT, healthGauge, setGaugeFill, styleHeroGauge, tickGauges } from './entities/health-gauge.js';
import { isAllyClickTarget, isEnemyClickTarget, pickUnit, type UnitPickTarget } from './entities/unit-picking.js';
import { selectUnitsInRectangle } from './entities/unit-selection.js';
import { CAMERA_ZOOM_RANGE, createIsometricCamera, fitIsometricDepth, resizeIsometricCamera } from './camera/isometric-camera.js';
import { CameraControls } from './camera/camera-controls.js';
import * as THREE from 'three';
import './light-culling.js';
import type { HeroId, Point, WorldSnapshot } from '../../shared/network/protocol.js';
import type { SelectionMode } from '../presentation/party/hero-selection.js';
import { isSelectionDrag } from '../presentation/party/selection-gesture.js';
import { CombatEffects } from './combat/combat-effects.js';
import { ChanneledPreviews } from './combat/channeled-previews.js';
import { CantripCharges } from './combat/cantrip-charges.js';
import { MonsterChannels, isMonsterSpell } from './combat/monster-channels.js';
import type { CombatVisualEvent } from '../../shared/game/combat-types.js';
import { SpellChannels, orbLightCount, pixelsPerMeter } from './combat/spell-channels.js';
import { smoothCast } from './entities/cast-interpolation.js';
import { graphicsOptions } from './graphics-options.js';
import { FIGURE_DETAIL } from './entities/create-unit-animator.js';
import { CharacterAnimationEvents } from './entities/character-animation.js';
import { isCombatPoseActive } from './entities/character-animator.js';
import type { UnitAnimator } from './entities/unit-animator.js';
import type { MapVisual } from './environment/coast-visual.js';
import { CLOCK_START_MINUTES } from '../../shared/game/rest-types.js';
import { createSunsetLighting } from './environment/sunset-lighting.js';
import { FogOfWarOverlay } from './environment/fog-of-war.js';
import { CharacterOcclusion, type CutoutHero } from './environment/character-occlusion.js';
import { WallsDown } from './environment/walls-down.js';
import { enableTerrainPicking, pickTerrainGround, setTerrainSurface } from './environment/terrain-picking.js';
import type { CaveView } from './environment/cave-view.js';
import { levelHeight, levelMap, spawnLevel, standingLevel } from '../../shared/game/caves.js';
import { CoastChart } from '../presentation/exploration/coast-chart.js';
import { PingMarks } from './pings/ping-marks.js';
import { StealthMarks } from './environment/stealth-marks.js';
import { ShadowFigures } from './entities/shadow-figures.js';
import { playPing } from '../audio/ping-sounds.js';
import type { PingMark } from '../../shared/network/pings.js';
import { CombatSounds, playStep } from '../audio/combat-sounds.js';
import { placeSound, placeVoice, type VoicePlacement } from '../audio/voice-director.js';
import { playCastingVoice, playMonsterCastingVoice, prepareCastingVoice, prepareMonsterCastingVoice } from '../audio/casting-voice.js';
import { createChannelSounds } from '../audio/channel-sounds.js';
import { Surfaces } from '../audio/surfaces.js';
import { playThunder } from '../audio/thunder.js';
import { playCritter } from '../audio/critter-sounds.js';
import { LobbyPatrol, lobbyCrew } from './entities/lobby-patrol.js';
import { stockFigureHeads, type HeadStock } from './entities/head-pool.js';
import { bakedFigures } from './entities/figure-bakes.js';
import { residentUnit } from './entities/resident-figure.js';
import { CivilianActors } from './entities/civilian-actors.js';
import { CritterField } from './critters/critter-field.js';
import { CritterGround } from './critters/critter-ground.js';
import { conditions, setConditions, type WeatherPreset } from './environment/conditions.js';
import { WeatherLayer } from './environment/weather.js';
import { GroundTraces } from './environment/ground-traces.js';
import { TrampleTrail } from './environment/trample.js';
import { SupplyMarkers, type SupplyHandlers } from './supplies/supply-markers.js';
import { showPropStates } from './environment/prop-states.js';
import { OrderMarks } from './environment/order-marks.js';
import { warmScene, type Drawable } from './scene-warmup.js';
import { retainShaderPrograms } from './program-retention.js';
import type { SharedRenderer } from './shared-renderer.js';
import { corpseShaderWarmup } from './entities/settled-corpse.js';
import { SummonSpares } from './summon-spares.js';
import { gameContent } from '../../content/game-content.js';
import { IdlePreparation, preparationFrame } from './idle-preparation.js';
import { PICTURE_FILTERS, PictureFilterPass, pictureFiltersActive } from './picture-filters.js';
import { UnitHoverOutline } from './combat/unit-hover-outline.js';
import { markPerformance } from '../performance-mark.js';
import { releaseObjects } from './release-objects.js';
import { FigurePresence } from './entities/unit-reveal.js';
import { ChargeTrails } from './entities/charge-trail.js';
import { UnitViewCulling, needsUnitPose } from './entities/unit-view-culling.js';
import { GlancePicker, type GlanceUnit } from './entities/head-glance.js';
import { StrikeTiming } from './combat/strike-timing.js';
import { EncounterBanners } from './entities/encounter-banners.js';
import { loadSoloClips } from './entities/solo-clip-library.js';
import { matches } from '../input/keymap.js';

/**
 * JSON with numbers rounded to a millionth. Maps and spawns are generated by the same code on the server (Node) and in the browser, whose
 * floating point differs in the last digit, so content compared across the two must not be compared exactly.
 */
const stableJson = (value: unknown) => JSON.stringify(value, (_key, item) => typeof item === 'number' ? Math.round(item * 1e6) / 1e6 : item);
/** Share of the loading bar spent dressing figures; the rest is compiling and uploading the world. */
const DRESS_SHARE = .3;
/** Figures are built for this long at most before the loading overlay gets a frame. */
const BUILD_SLICE_MS = 30;
/** Metres a Ctrl-drag must travel from the ordered point before it turns the formation. */
const ORIENT_SLOP = 1.2;
/** How strongly a unit's ground ring shows when it is neither selected nor targeted: a hint, so the selected and targeted stand out. */
const FAINT_RING = .5;
/** How much of an enemy's three-tier gauge each condition lights. */
const CONDITION_FILL = { healthy: 1, bloodied: 2 / 3, 'near-death': 1 / 3, dead: 0 } as const;
/**
 * Opaque draw order: three's group and render order first, then by shader program, then by material, nearest first within one. Each
 * program switch re-sends the camera, lights and every material uniform; figures sharing materials still draw with dozens of programs
 * between them, and ordered by material alone the program changed on almost every other draw. `program` reads a material's current
 * program id (0 before it has one), cached for the frame.
 */
function opaqueOrder(program: (material: THREE.Material) => number) {
  return (a: THREE.RenderItem, b: THREE.RenderItem) => a.groupOrder - b.groupOrder || a.renderOrder - b.renderOrder || program(a.material) - program(b.material)
    || (a.material as unknown as { id: number }).id - (b.material as unknown as { id: number }).id || a.z - b.z || a.id - b.id;
}
/** Poses a second at most for a unit idling out of combat: every frame at 60 fps, every other one at 144. */
const IDLE_POSE_RATE = 70;
/** Shadow-map redraws a second at most: every frame at 60 fps, every other one at 100 and every third at 144. */
const SHADOW_RATE = 62;

export class GameScene {
  /** Plain diagnostics only: never expose scene objects or authoritative controls. */
  /** The renderer and world, for the console's diagnostics (`game.programs()`), which production never loads. */
  diagnosticRendering() { return { renderer: this.renderer, scene: this.scene }; }
  renderingStats() {
    const heap = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    return { gameTime: this.renderedGameTime, paused: this.latest?.paused ?? null, prepared: this.prepared, disposed: this.disposed,
      units: { known: this.units.size, snapshot: (this.latest?.heroes.length ?? 0) + (this.latest?.enemies.length ?? 0), presented: [...this.units.values()].filter(unit => unit.visible).length },
      resources: { ...this.renderer.info.memory, programs: this.renderer.info.programs?.length ?? 0 },
      draw: { ...this.renderer.info.render },
      jsHeap: heap ? { usedBytes: heap.usedJSHeapSize, allocatedBytes: heap.totalJSHeapSize, limitBytes: heap.jsHeapSizeLimit } : null,
      contextLost: this.renderer.getContext().isContextLost() };
  }
  private frameObserver: { begin(now: number): void; phase(name: string): void; beforeDraw(): void; end(): void; measure?(name: string, ms: number): void;
    animation?: boolean; effects?: boolean; rendering?: boolean; outlines?: boolean; hud?: boolean } | null = null;
  /** Opt-in frame instrumentation shared by the game console and scenario tools. */
  observeFrames(observer: GameScene['frameObserver']) {
    this.frameObserver = observer;
    return { renderer: this.renderer, scene: this.scene, camera: this.camera, animators: this.animators, characters: this.units,
      effectObjects: () => [...this.effects.visualObjects, ...this.channels.visualObjects, ...this.cantrips.visualObjects],
      effectLights: () => this.channels.lightObjects,
      profileState: () => ({ gameTime: this.renderedGameTime, paused: this.latest?.paused,
        heroes: this.latest?.heroes.length ?? 0, enemies: this.latest?.enemies.length ?? 0,
        presentedUnits: [...this.units.values()].filter(group => group.visible).length,
        deadHeroes: this.latest?.heroes.filter(unit => unit.health.dead).length ?? 0,
        deadEnemies: this.latest?.enemies.filter(unit => unit.health.dead).length ?? 0 }) };
  }
  private pendingUnits: Promise<void>[] = [];
  /** Heads meshed ahead on workers for the figures `prepare` builds next (`head-pool.ts`); released once they are built. */
  private headStocks: { stock: HeadStock; adoption: number }[] = [];
  /** Counts `adopt`s: a preparation releases only the stocks of its own adoption and earlier, not those of a party adopted meanwhile. */
  private adoption = 0;
  /** What warming has drawn on the current context, so preparing again (a party adopted into the lobby) uploads only what is new. */
  private uploaded = new WeakSet<THREE.Object3D>();
  /** Encounter figures' heads stocked ahead of `prewarmEnemies` (`expectEnemies`). */
  private enemyHeads: HeadStock | null = null;
  /** Heroes the constructor and `adopt` leave for `prepare` to build, one a frame behind the loading overlay. */
  private deferredHeroes: UnitDefinition[] = [];
  private disposed = false;
  private prepared = false;
  private unitView = new UnitViewCulling();
  private unitOpacity = new WeakMap<THREE.Group, number>();
  private preparation: Promise<void> = Promise.resolve();
  private renderer: THREE.WebGLRenderer;
  private releasePrograms: () => void;
  private summonSpares: SummonSpares | null = null;
  private scene = new THREE.Scene();
  /** Spark Step and Sneak hide or dissolve figures; a charging Ratrider streaks. Presentation only. */
  private presence = new FigurePresence();
  private chargeTrails = new ChargeTrails(this.scene, id => this.animators.get(id)?.rig.height ?? 1.6);
  private camera = createIsometricCamera();
  private cameraGroundHeight = 0;
  private cameraControls: CameraControls;
  /** Set by development tools: the view may leave the map at any zoom, not only past the game's widest. */
  private looseView = false;
  private lastFrame = performance.now();
  private viewport = { width: 1, height: 1 };
  private raycaster = new THREE.Raycaster();
  private pointerNdc = new THREE.Vector2();
  private units = new Map<HeroId, THREE.Group>();
  private unitDefinitions = new Map<HeroId, string>();
  private enemyPreparation: IdlePreparation<EnemyDefinition> | null = null;
  private figures = new Map<HeroId, THREE.Object3D>();
  private hoverOutline: UnitHoverOutline;
  private selectingUnits = false;
  private selectionDrag: { start: { x: number; y: number }; end: { x: number; y: number }; additive: boolean } | null = null;
  private pickTargets: UnitPickTarget[] = [];
  private animators = new Map<HeroId, UnitAnimator>();
  private animationEvents = new CharacterAnimationEvents(this.animators);
  private strikeTiming = this.createStrikeTiming();
  private createStrikeTiming() { return new StrikeTiming((event, at) => this.animators.get(event.actorId)?.swingLead?.(at) ?? 0); }
  private labelHeights = new Map<HeroId, number>();
  private labels = new Map<HeroId, HTMLDivElement>();
  /** Cursor position relative to the canvas, for fading hero plates. */
  private pointer: { x: number; y: number } | null = null;
  private hoveredId: string | null = null;
  /** The visible unit picked for this frame, shared with the HUD without another raycast. */
  get hoveredUnitId() { return this.hoveredId; }
  private rings = new Map<HeroId, THREE.Mesh>();
  private selectionGlyphs = new Map<HeroId, THREE.Mesh>();
  private outlineFigures: THREE.Object3D[] = [];
  private samples: { at: number; snapshot: WorldSnapshot }[] = [];
  private frameStates: UnitState[] = [];
  private glanceUnits: GlanceUnit[] = [];
  private glancePicker = new GlancePicker();
  private earlierStates: UnitState[] = [];
  private tramplers: { id: string; x: number; z: number }[] = [];
  private ownHeroes: HeroId[] = [];
  private selectedHeroes: HeroId[] = [];
  /** The shape the party's next move takes; the HUD sets it, and the Ctrl-drag preview lays it out. */
  formationShape: FormationShape = 'line';
  /**
   * A Ctrl-drag in progress: the ordered point, the way the pointer has turned the formation round it (none until it moves far enough),
   * and the hand its slot order runs toward as last drawn, so the order sent is the layout shown.
   */
  private orienting: { pointerId: number; anchor: Point & { level?: number }; facing?: number; hand?: 1 | -1 } | null = null;
  private selectionBox = document.createElement('div');
  /** The page layer for everything drawn over the world (plates, callouts, supply labels), clipped around the HUD's 3D pieces. */
  private overlays = Object.assign(document.createElement('div'), { className: 'world-overlays' });
  private orders = new Map<HeroId, OrderMarks>();
  private mapVisual: MapVisual;
  private sunset: ReturnType<typeof createSunsetLighting> | null = null;
  private weather: WeatherLayer | null = null;
  /** What walking leaves behind: prints in sand, dust on roads, ripples at the tide line, and grass that bends away from walkers. */
  private traces: GroundTraces | null = null;
  private trample = new TrampleTrail();
  /** Each unit's last place, the way it is heading and which foot lands next, worked out from its movement. */
  private gait = new Map<string, { x: number; z: number; heading: number; foot: -1 | 1 }>();
  private forcedWeather: WeatherPreset | null = null;
  private sky: number | null = null;
  private skyAt = performance.now();
  private chart: CoastChart | null = null;
  /** The map chart, which the 3D HUD shows; null on maps without scenery. */
  get mapChart() { return this.chart; }
  private fogOverlay: FogOfWarOverlay;
  private characterOcclusion = new CharacterOcclusion();
  private cutoutHeroes: CutoutHero[] = [];
  private wallsDown!: WallsDown;
  /** How the map's caves are drawn under their roofs; absent on a map without caves. */
  private caves?: CaveView;
  /** The level each unit walked in the last snapshot drawn, for its feet. */
  private unitLevels = new Map<string, number>();
  private mapRoots = new Set<THREE.Object3D>();
  private wallsDownHeroes: THREE.Vector3[] = [];
  private resizeObserver = new ResizeObserver(() => this.resize());
  private latest: WorldSnapshot | null = null;
  private effects: CombatEffects;
  private previewCasts = new ChanneledPreviews();
  private monsterCasts = new MonsterChannels(id => this.content.enemies.find(enemy => enemy.id === id)?.profile.attacks);
  private renderedGameTime = 0;
  private channels: SpellChannels;
  private torches: Torches;
  private cantrips: CantripCharges;
  private channelSounds = createChannelSounds();
  private unwatchGraphics = () => {};
  /**
   * Lights for the orbs: one for every hero, who are the casters there are, up to the player's limit. (A unit that is not a hero and can cast, when
   * there is one, counts here too.) The lights exist from the start so that casting never changes the number of lights in the scene.
   */
  private orbLights() {
    // The lobby has no enemies yet: it counts the casters it was told to expect, so a party adopted into it keeps every lit shader.
    const enemies = this.content.enemies.length ? this.content.enemies : this.expectedEnemies;
    return orbLightCount(graphicsOptions.spellLightLimit, this.content.heroes.length + enemies.filter(enemy => enemy.profile.attacks.some(attack => isMonsterSpell(attack.id))).length);
  }
  private expectedEnemies: readonly EnemyDefinition[] = [];
  private markers: SupplyMarkers;
  private pings: PingMarks;
  private stealth: StealthMarks;
  /** Hidden heroes drawn as dark, flickering shapes. Presentation only. */
  private shadows = new ShadowFigures();
  private banners: EncounterBanners;
  private patrol: LobbyPatrol | null = null;
  private civilians: CivilianActors;
  private critters: CritterField | null = null;
  private combatSounds = new CombatSounds();
  private surfaces: Surfaces;
  private splashLive = false;
  /** The sun of maps without the coast's lighting. */
  private plainSun: THREE.DirectionalLight | null = null;
  private shadowDrawnAt = -Infinity;
  /** Game time each unit was last posed at, for the idle pose rate. */
  private posedAt = new Map<string, number>();
  private disposeTab = () => {};
  constructor(private host: HTMLElement, onMove: (point: Point, turn?: { facing: number; hand: 1 | -1 }) => void, private content: GameContent,
    private onAttack: (id: string) => void = () => {}, private onSelect: (ids: HeroId[], mode: SelectionMode) => void = () => {}, supply: SupplyHandlers = { loot() {}, merchant() {}, read() {}, use() {} }, private enemyTargetingAllowed: () => boolean = () => true,
    private allyTargetingAllowed: (id: string) => boolean = id => this.ownHeroes.includes(id),
    /** A renderer shared by a page that opens one scene after another: it stays, with its linked shaders, when this scene is disposed. */
    private sharedRenderer: SharedRenderer | null = null) {
    this.renderer = sharedRenderer?.acquire() ?? new THREE.WebGLRenderer({ antialias: true, stencil: true });
    this.releasePrograms = sharedRenderer ? () => {} : retainShaderPrograms(this.renderer);
    void loadSoloClips();
    // Workers mesh the residents' and enemies' heads while the map is laid out; the figures themselves are built by `prepare`, which stocks
    // the heroes' heads too unless it prepares the lobby.
    this.stockHeads([...(content.map.civilians ?? []).map(resident => residentUnit(resident).definition), ...content.enemies]);
    this.combatSounds = this.createCombatSounds(content.enemies);
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = graphicsOptions.shadows; this.renderer.shadowMap.type = THREE.PCFShadowMap;
    castOnlyIntoShadows(this.renderer);
    // Three sorts opaque draws front to back, so consecutive draws keep changing program and material and every uniform is sent again
    // each time (about half of a frame's draw submission at the spawn). Grouped by both, the view from above loses little to overdraw.
    const properties = this.renderer.properties, programs = new WeakMap<THREE.Material, { frame: number; id: number }>();
    this.renderer.setOpaqueSort(opaqueOrder(material => {
      const frame = this.renderer.info.render.frame, known = programs.get(material);
      if (known?.frame === frame) return known.id;
      const id = (properties.get(material) as { currentProgram?: { id: number } }).currentProgram?.id ?? 0;
      programs.set(material, { frame, id });
      return id;
    }));
    this.renderer.setClearColor(0x111c20);
    this.renderer.domElement.setAttribute('aria-label', 'Isometric map. Drag to select your heroes. Click a hero to select, ground to move, or an enemy to attack. Move the mouse to a screen edge or middle-mouse drag to pan. Scroll to zoom.');
    this.host.append(this.renderer.domElement, this.overlays);
    this.effects = new CombatEffects(this.scene, this.overlays, [...content.heroes, ...content.enemies], (x, z) => this.groundHeight(x, z));
    this.effects.onPreviewLand = (abilityId, at) => this.combatSounds.previewCantrip(abilityId, 'land', at);
    this.channels = new SpellChannels(this.scene, undefined, this.orbLights());
    this.torches = new Torches(this.scene); this.torches.setLimit(graphicsOptions.torchLightLimit);
    this.cantrips = new CantripCharges(this.scene, (x, z) => this.groundHeight(x, z));
    this.stealth = new StealthMarks(this.scene, this.content.map, (x, z) => this.groundHeight(x, z));
    this.pings = new PingMarks(this.scene, (x, z) => this.groundHeight(x, z), id => this.units.get(id) ?? null, mesh => conformTerrainDecal(mesh, this.content.map));
    for (const hero of content.heroes) prepareCastingVoice(hero);
    for (const enemy of content.enemies) prepareMonsterCastingVoice(enemy);
    const channelSounds = this.channelSounds;
    this.channels.onStart = (id, school, seconds) => {
      channelSounds.start(id);
      const hero = this.content.heroes.find(hero => hero.id === id);
      if (hero) { playCastingVoice(hero, school.id, seconds, this.voicePlacement(id)); return; }
      const enemy = this.content.enemies.find(enemy => enemy.id === id);
      if (enemy) playMonsterCastingVoice(enemy, school.id, this.voicePlacement(id));
    };
    this.channels.onUpdate = (id, elapsed, duration) => {
      const place = this.voicePlacement(id);
      channelSounds.update(id, elapsed, duration, this.latest?.paused ?? false, place?.strength ?? 1, place?.pan ?? 0);
    };
    this.channels.onStop = (id, completed) => channelSounds.stop(id, completed);
    this.unwatchGraphics = graphicsOptions.onChange(() => this.followLighting());
    this.effects.severHead = id => this.animators.get(id)?.sever?.();
    this.scene.fog = new THREE.Fog(0x111c20, 110, 180);
    this.camera.position.set(26, 26, 26); this.camera.lookAt(0, 0, 0);
    if (content.map.scenery?.theme === 'sunset-coast') this.sunset = createSunsetLighting(this.scene, this.renderer);
    else {
    this.scene.add(new THREE.HemisphereLight(0xe2e9d5, 0x26383c, 2.1));
    const sun = new THREE.DirectionalLight(0xffe4bd, 3);
    sun.position.set(-12, 24, 10); sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024); Object.assign(sun.shadow.camera, { left: -40, right: 40, top: 40, bottom: -40 });
    sun.shadow.normalBias = 0.03; this.scene.add(sun); this.plainSun = sun;
    }
    this.surfaces = new Surfaces(content.map);
    const beforeMap = new Set(this.scene.children);
    this.mapVisual = createMapVisual(this.scene, content.map);
    this.characterOcclusion.setTerrain(this.mapVisual.ground);
    this.caves = this.mapVisual.caves;
    this.wallsDown = new WallsDown(content.map);
    for (const material of Array.isArray(this.mapVisual.ground.material) ? this.mapVisual.ground.material : [this.mapVisual.ground.material]) this.wallsDown.adoptTerrain(material);
    setTerrainSurface(this.mapVisual.ground, (x, z, height) => this.wallsDown.surface(x, z, height));
    this.mapVisual.caveGrounds?.forEach((floor, index) => { enableTerrainPicking(floor, levelMap(content.map, index + 1)); setTerrainSurface(floor, (x, z, height) => this.wallsDown.surface(x, z, height)); });
    for (const root of this.scene.children) if (!beforeMap.has(root)) {
      this.mapRoots.add(root); this.characterOcclusion.adopt(root); this.wallsDown.adoptScenery(root); this.caves?.adoptScenery(root);
    }
    if (this.sunset) {
      const terrain = new CritterGround(content.map);
      this.weather = new WeatherLayer(this.scene, this.renderer, content.map, terrain, this.mapVisual.ground, content.map.scenery?.seed ?? 0); this.weather.onThunder = playThunder;
      this.traces = new GroundTraces(this.scene, content.map, terrain);
    }
    this.civilians = new CivilianActors(this.scene, content.map.civilians ?? [], content.map);
    if (content.map.critters) {
      this.critters = new CritterField(this.scene, this.camera, content.map, content.map.critters);
      this.critters.onSound = (cue, at) => { const { strength, pan } = this.soundPlacement(at); if (strength > 0) playCritter(cue, strength, pan); };
    }
    this.markers = new SupplyMarkers(this.scene, this.overlays, content, supply);
    this.banners = new EncounterBanners(this.scene); this.banners.setDefinitions(content.enemies);
    this.fogOverlay = new FogOfWarOverlay(this.scene, content.map); this.fogOverlay.visible = false;
    this.hoverOutline = new UnitHoverOutline(this.renderer, this.scene, this.camera);
    if (content.map.scenery) this.chart = new CoastChart(host, content, point => this.focusCamera(point), () => this.focusParty());
    this.deferredHeroes.push(...content.heroes);
    this.queueEnemies(content.enemies);
    this.selectionBox.className = 'selection-box'; this.selectionBox.hidden = true; host.append(this.selectionBox);
    const canvas = this.renderer.domElement;
    canvas.addEventListener('webglcontextlost', this.contextLost);
    canvas.addEventListener('webglcontextrestored', this.contextRestored);
    let selecting: { x: number; y: number; additive: boolean; pointerType: string; pointerId: number } | null = null;
    canvas.addEventListener('pointerdown', event => {
      if (event.button !== 0 || !this.ownHeroes.length || this.cameraControls.dragging) return;
      // Ctrl-drag on open ground orders a move and turns the formation about it as the pointer swings.
      // A captured mouse (pointer lock) sends its own events to the canvas and cannot be captured again.
      if (event.ctrlKey && this.selectedHeroes.length) {
        const ground = this.groundAt(event.clientX, event.clientY);
        if (ground && pickUnit(this.raycaster, this.pickTargets) === undefined && !this.markers.pick(this.raycaster)) {
          this.orienting = { pointerId: event.pointerId, anchor: this.moveTarget(ground) };
          if (!document.pointerLockElement) canvas.setPointerCapture(event.pointerId);
          return;
        }
      }
      selecting = { x: event.clientX, y: event.clientY, additive: event.shiftKey, pointerType: event.pointerType, pointerId: event.pointerId };
      this.selectingUnits = true;
      this.selectionDrag = { start: { x: event.clientX, y: event.clientY }, end: { x: event.clientX, y: event.clientY }, additive: event.shiftKey };
      if (!document.pointerLockElement) canvas.setPointerCapture(event.pointerId);
    });
    canvas.addEventListener('pointermove', event => { const box = canvas.getBoundingClientRect(); this.pointer = { x: event.clientX - box.left, y: event.clientY - box.top }; });
    canvas.addEventListener('pointerleave', () => { this.pointer = null; });
    canvas.addEventListener('pointermove', event => {
      const orienting = this.orienting;
      if (orienting && event.pointerId === orienting.pointerId) {
        const ground = this.groundAt(event.clientX, event.clientY);
        if (ground && Math.hypot(ground.x - orienting.anchor.x, ground.z - orienting.anchor.z) >= ORIENT_SLOP) orienting.facing = Math.atan2(ground.x - orienting.anchor.x, ground.z - orienting.anchor.z);
        return;
      }
      if (!selecting && event.pointerType === 'mouse') {
        const rect = canvas.getBoundingClientRect();
        this.pointerNdc.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1);
        this.raycaster.setFromCamera(this.pointerNdc, this.camera);
        const picked = pickUnit(this.raycaster, this.pickTargets);
        // What a click would do here, for the cursor (`styles/cursors.css` draws it, armed powers included).
        const under = picked !== undefined ? this.canTargetEnemy(picked) ? 'attack' : this.canTargetAlly(picked) ? 'ally' : 'unit'
          : this.markers.hover(this.raycaster) ? 'pickup' : this.selectedHeroes.length ? 'ground' : 'none';
        canvas.dataset.cursor = under;
        if (picked !== undefined) this.markers.hover(null);
      }
      if (!selecting || event.pointerId !== selecting.pointerId) return;
      if (this.selectionDrag) this.selectionDrag.end = { x: event.clientX, y: event.clientY };
      this.selectionBox.hidden = !isSelectionDrag(selecting, { x: event.clientX, y: event.clientY }, selecting.pointerType);
      if (this.selectionBox.hidden) return;
      const rect = host.getBoundingClientRect(); this.selectionBox.hidden = false;
      Object.assign(this.selectionBox.style, { left: Math.min(selecting.x, event.clientX) - rect.left + 'px', top: Math.min(selecting.y, event.clientY) - rect.top + 'px', width: Math.abs(event.clientX - selecting.x) + 'px', height: Math.abs(event.clientY - selecting.y) + 'px' });
    });
    canvas.addEventListener('pointerup', event => {
      const orienting = this.orienting;
      if (orienting && event.pointerId === orienting.pointerId && event.button === 0) {
        this.orienting = null;
        if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
        onMove(orienting.anchor, orienting.facing === undefined || !orienting.hand ? undefined : { facing: orienting.facing, hand: orienting.hand });
        return;
      }
      if (!selecting || event.pointerId !== selecting.pointerId || event.button !== 0) return;
      const start = selecting; selecting = null; this.selectionBox.hidden = true;
      this.selectingUnits = false;
      this.selectionDrag = null;
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      const rect = canvas.getBoundingClientRect();
      if (isSelectionDrag(start, { x: event.clientX, y: event.clientY }, start.pointerType)) {
        const ids = this.rectangleHeroes(start, { x: event.clientX, y: event.clientY });
        if (ids.length > 0) this.onSelect(ids, start.additive ? 'add' : 'replace'); // An empty drag keeps the current selection.
        return;
      }
      this.raycaster.setFromCamera(new THREE.Vector2((start.x - rect.left) / rect.width * 2 - 1, -(start.y - rect.top) / rect.height * 2 + 1), this.camera);
      const pickStart = performance.now();
      const id = pickUnit(this.raycaster, this.pickTargets);
      performance.measure('nimblez:unit-pick', { start: pickStart, end: performance.now() });
      performance.clearMeasures('nimblez:unit-pick'); // Keep traces useful without retaining entries all session.
      if (id !== undefined) {
        if (this.ownHeroes.includes(id)) { if (this.canTargetAlly(id)) this.onSelect([id], start.additive ? 'toggle' : 'replace'); return; }
        // Another player's hero: never changes the selection, but an armed ability may pick it as its ally.
        if (this.content.heroes.some(hero => hero.id === id)) { if (this.canTargetAlly(id)) this.onSelect([id], 'add'); return; }
        if (this.canTargetEnemy(id)) onAttack(id);
        return;
      }
      const interact = this.markers.pick(this.raycaster);
      if (interact) { interact(); return; }
      const ground = this.content.map.terrain ? this.pickGround()
        : this.raycaster.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), new THREE.Vector3());
      // Under a roof the point names its level: the cave below the ceiling, the rock's top above it.
      if (ground) { onMove(this.moveTarget(ground)); return; }
      // The ray missed the terrain mesh (past its edge, a gap): the server walks to the nearest reachable ground to where it meets the flat plane.
      const flat = this.raycaster.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), new THREE.Vector3());
      const { width, depth } = this.content.map;
      if (flat) onMove({ x: Math.max(-width / 2, Math.min(width / 2, flat.x)), z: Math.max(-depth / 2, Math.min(depth / 2, flat.z)) });
    });
    const typing = (event: KeyboardEvent) => event.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName);
    // Held Tab lights what can be used and brings every unit's plate up.
    const tabDown = (event: KeyboardEvent) => { if (matches(event, 'plates') && !typing(event)) { event.preventDefault(); this.markers.setHighlight(true); this.platesHeld = true; } };
    const tabUp = (event: KeyboardEvent) => { if (matches(event, 'plates')) { this.markers.setHighlight(false); this.platesHeld = false; } };
    const tabRelease = () => { this.markers.setHighlight(false); this.platesHeld = false; };
    addEventListener('keydown', tabDown); addEventListener('keyup', tabUp); addEventListener('blur', tabRelease);
    this.disposeTab = () => { removeEventListener('keydown', tabDown); removeEventListener('keyup', tabUp); removeEventListener('blur', tabRelease); };
    const cancelSelection = () => { selecting = null; this.selectingUnits = false; this.selectionDrag = null; this.selectionBox.hidden = true; };
    canvas.addEventListener('pointerleave', () => { this.markers.hover(null); delete canvas.dataset.cursor; }); canvas.addEventListener('pointercancel', cancelSelection); canvas.addEventListener('lostpointercapture', cancelSelection);
    const cancelOrienting = () => { this.orienting = null; };
    canvas.addEventListener('pointercancel', cancelOrienting); canvas.addEventListener('lostpointercapture', cancelOrienting);
    this.cameraControls = new CameraControls(this.camera, canvas, () => this.ownHeroes.length > 0, () => selecting !== null);
    this.resizeObserver.observe(host); this.resize();
  }
  /**
   * Builds every figure again at the current Character detail (`graphics-options.ts`), then prepares the scene as on entry: the caller shows
   * the loading overlay meanwhile, and the snapshots in hand pose the new figures where the old ones stood. Prepared summon spares go too.
   */
  async rebuildFigures(lobby = false, status: (message: string, progress: number) => void = () => {}) {
    const ids = [...this.unitDefinitions.keys()];
    this.stockHeads(ids.map(id => JSON.parse(this.unitDefinitions.get(id)!) as UnitDefinition));
    for (let i = 0; i < ids.length; i++) {
      // One figure a frame, once workers have its head at the new detail: the connection must stay served.
      status('Dressing the characters…', DRESS_SHARE * i / ids.length);
      await this.headsOf(ids[i]); await preparationFrame();
      if (this.disposed) return;
      const json = this.unitDefinitions.get(ids[i]), group = this.units.get(ids[i]); if (!json || !group) continue;
      const definition = JSON.parse(json) as UnitDefinition, visible = group.visible, position = group.position.clone();
      this.removeUnit(ids[i]); this.addUnit(definition);
      const rebuilt = this.units.get(ids[i])!; rebuilt.position.copy(position); rebuilt.visible = visible;
    }
    this.summonSpares?.dispose(); this.summonSpares = null;
    await this.prepare(lobby, status);
  }
  /**
   * Rendering and input begin only after procedural figures and GPU resources are ready. May run again after `adopt`;
   * the frame loop pauses meanwhile because warming shows every object, including the ones that are hidden on purpose.
   */
  prepare(lobby = false, status: (message: string, progress: number) => void = () => {}) {
    const preparing = this.preparation.then(() => this.prepareScene(lobby, status));
    this.preparation = preparing.catch(() => {});
    return preparing;
  }
  private async prepareScene(lobby: boolean, status: (message: string, progress: number) => void) {
    if (this.disposed) return;
    const preparing = this.adoption;
    this.prepared = false; this.renderer.setAnimationLoop(null); this.preparedLobby = lobby;
    this.effects.prepareSpells();
    // Authored stances, idles and resident activities (`solo-clip-library.ts`): started with the scene, needed before its first frame.
    await loadSoloClips();
    if (this.disposed) return;
    const patrol = lobby && !this.patrol ? lobbyCrew() : null;
    if (patrol) this.stockHeads(patrol);
    // The lobby's seats hold the content's own heroes, which a party always replaces (`adopt`): they keep their seats unbuilt.
    if (lobby) this.deferredHeroes = []; else this.stockHeads(this.deferredHeroes);
    // Dressing is counted per figure; warming reports its own share.
    // Unbuilt figures (heroes, encounter figures) are two steps each: built, then dressed; residents are built only, and the patrol is dressed once.
    const dressing = { done: 0, total: this.pendingUnits.length + (patrol ? 1 : 0) + this.civilians.remaining + 2 * (this.deferredHeroes.length + (this.enemyPreparation?.remaining ?? 0)) };
    const dressed = (message = 'Dressing the characters…') => status(message, DRESS_SHARE * Math.min(1, dressing.done / Math.max(1, dressing.total)));
    const tally = (ready: Promise<void>) => ready.then(() => { dressing.done++; dressed(); });
    dressed(); markPerformance('nimblez:dressing');
    // Each figure is built once the workers have its head, then takes 50–300 ms more: yielding a frame between slices of them keeps the
    // overlay painting and the connection served.
    let slice = performance.now();
    const build = async (id: string, add: () => void) => {
      await this.headsOf(id);
      if (performance.now() - slice > BUILD_SLICE_MS) { await preparationFrame(); slice = performance.now(); }
      if (this.disposed) return false;
      add(); dressing.done++; dressed();
      return true;
    };
    for (let resident = this.civilians.next; resident; resident = this.civilians.next) if (!await build(resident.id, () => this.civilians.buildNext())) return;
    if (patrol) {
      await Promise.all(patrol.map(member => this.headsOf(member.id))); await preparationFrame();
      if (this.disposed) return;
      this.patrol = new LobbyPatrol(this.scene, this.content.map); this.pendingUnits.push(this.patrol.ready);
    }
    for (let hero = this.deferredHeroes.shift(); hero; hero = this.deferredHeroes.shift()) if (!await build(hero.id, () => this.addUnit(hero))) return;
    await Promise.all(this.headStocks.map(({ stock }) => stock.ready));
    if (this.disposed) return;
    await this.enemyPreparation?.finish(() => { dressing.done++; dressed(); });
    (this.summonSpares ??= new SummonSpares(this.scene, { ...gameContent.enemySummons, ...this.content.enemySummons })).prepare(this.content.enemies, this.content.heroes.flatMap(hero => {
      const definition = this.content.classes?.find(candidate => candidate.id === hero.classId) ?? gameContent.classes?.find(candidate => candidate.id === hero.classId);
      return definition?.abilities.flatMap(ability => ability.summon && ability.level <= hero.level ? [ability.summon.template] : []) ?? [];
    }));
    do {
      const pending = this.pendingUnits.splice(0);
      this.lightingChanged = false;
      // Units that arrive while shaders compile (a second account joining), and lights changed meanwhile, are picked up by the next pass.
      dressing.total = Math.max(dressing.total, dressing.done + pending.length);
      dressed();
      await Promise.all(pending.map(tally));
      if (this.disposed) return;
      markPerformance('nimblez:units-dressed');
      status('Compiling the world…', DRESS_SHARE);
      this.fogOverlay.adoptScene();
      // Rigid stand-ins compile the shaders a settled corpse switches to.
      const corpses = corpseShaderWarmup(this.units.values()); this.scene.add(corpses);
      // So are a ping's ring, ripples, glyph and motes, which are otherwise built (and their shaders linked) by the first ping.
      const pings = this.pings.warmup(); this.scene.add(pings);
      const charge = CantripCharges.warmup(); this.scene.add(charge.group);
      // The grass around the view, so its program links here rather than on the first frame.
      this.mapVisual.grass?.stream(this.camera);
      try {
        await warmScene(this.renderer, this.scene, this.camera, () => !this.disposed, undefined,
          { prepare: async objects => {
              await this.characterOcclusion.prepare(this.renderer, this.scene, this.camera, objects, () => !this.disposed);
              await this.hoverOutline.prepare(this.figureDrawables(objects), () => !this.disposed);
            }, finish: () => this.hoverOutline.warm(() => !this.disposed),
            progress: fraction => status('Compiling the world…', DRESS_SHARE + (1 - DRESS_SHARE) * fraction), uploaded: this.uploaded });
      } finally { corpses.removeFromParent(); pings.removeFromParent(); this.pings.warmed(); charge.dispose(); }
    } while (!this.disposed && (this.pendingUnits.length || this.lightingChanged));
    if (this.disposed) return;
    // The HUD layer is a scene of its own, which the warming above never draws: link its shaders here, once per renderer.
    if (this.overlayWarm && !this.overlayWarmed) {
      status('Compiling the world…', 1);
      this.overlayWarmed = true;
      try { await this.overlayWarm(this.renderer, () => !this.disposed); } catch (error) { console.warn('The HUD layer could not be warmed; its shaders will link on first use.', error); }
      if (this.disposed) return;
    }
    status('Compiling the world…', 1);
    const built = this.headStocks.filter(({ adoption }) => adoption <= preparing);
    for (const { stock } of built) stock.release();
    this.headStocks = this.headStocks.filter(entry => !built.includes(entry));
    this.effects.spellsPrepared();
    this.warmedLighting.add(this.lightingKey());
    this.prepared = true; this.lastFrame = performance.now();
    this.renderer.setAnimationLoop(() => this.render());
  }
  /** Only figures are ever outlined, so only their vertex paths need an outline program. */
  private figureDrawables(objects: readonly Drawable[]) {
    const inside = new Set<THREE.Object3D>();
    for (const figure of this.figures.values()) figure.traverse(object => { inside.add(object); });
    return objects.filter(object => inside.has(object));
  }
  /**
   * The encounter figures `prewarmEnemies` will build, told to the lobby before it prepares: workers start on their heads after its own
   * figures', and their casters count toward the spell lights, so the lit shaders it links are the world's.
   */
  expectEnemies(enemies: readonly EnemyDefinition[]) {
    this.enemyHeads?.release();
    this.enemyHeads = stockFigureHeads(enemies, FIGURE_DETAIL[graphicsOptions.characterDetail], bakedFigures);
    this.expectedEnemies = enemies; this.channels.setLights(this.orbLights());
  }
  /**
   * Attach hidden encounter figures during menu idle time, once workers have meshed their heads; GPU preparation remains inside the entry gate.
   * `adopt` keeps only figures whose definitions are exactly the welcome's, so pass them as the server publishes them (`publicEnemy`).
   */
  prewarmEnemies(enemies: EnemyDefinition[]) {
    if (this.disposed || this.latest || this.ownHeroes.length) return;
    this.queueEnemies(enemies);
    const preparation = this.enemyPreparation, heads = this.enemyHeads ?? stockFigureHeads(enemies, FIGURE_DETAIL[graphicsOptions.characterDetail], bakedFigures);
    this.enemyHeads = null; this.headStocks.push({ stock: heads, adoption: this.adoption });
    void heads.ready.then(() => preparation?.start());
  }
  private stockHeads(definitions: readonly UnitDefinition[]) {
    const stock = stockFigureHeads(definitions, FIGURE_DETAIL[graphicsOptions.characterDetail], bakedFigures);
    this.headStocks.push({ stock, adoption: this.adoption });
    return stock;
  }
  private headsOf(id: string) { return Promise.all(this.headStocks.map(({ stock }) => stock.of(id))); }
  private queueEnemies(enemies: EnemyDefinition[]) {
    this.enemyPreparation?.cancel();
    this.enemyPreparation = new IdlePreparation(enemies.filter(enemy => !this.units.has(enemy.id)), enemy => {
      this.effects.setHeight(enemy.id, enemy.appearance?.height ?? 2);
      this.addUnit(enemy);
    });
  }

  /**
   * Takes another room's party into this already-warm scene instead of building and compiling a new one. Only a world
   * on the same map with the same supply points qualifies; existing encounter figures are preserved and reset,
   * while heroes are rebuilt (by `prepare`, one a frame) so no animation state from the previous room survives. Call `prepare` afterwards.
   */
  adopt(next: GameContent) {
    const stage = (content: GameContent) => stableJson([content.map, content.merchants ?? [], content.safePlaces ?? []]);
    if (this.disposed || stage(next) !== stage(this.content)) return false;
    this.enemyPreparation?.cancel(); this.adoption++;
    this.clearWorld(); this.deferredHeroes = [];
    const nextUnits = [...next.heroes, ...next.enemies];
    const nextDefinitions = new Map(nextUnits.map(unit => [unit.id, stableJson(unit)]));
    // Remove units no longer in next content, and always remove existing heroes so the new party's classes/models are rebuilt fresh.
    for (const id of [...this.units.keys()]) {
      const known = this.unitDefinitions.get(id);
      if (next.heroes.some(h => h.id === id) || !known || stableJson(JSON.parse(known)) !== nextDefinitions.get(id)) {
        this.removeUnit(id);
      }
    }
    this.summonedIds.clear();
    this.content = next; this.channels.setLights(this.orbLights()); this.banners.setDefinitions(next.enemies);
    for (const enemy of next.enemies) prepareMonsterCastingVoice(enemy);
    this.animationEvents = new CharacterAnimationEvents(this.animators); this.strikeTiming = this.createStrikeTiming(); this.combatSounds = this.createCombatSounds(next.enemies);
    for (const definition of nextUnits) {
      this.effects.setHeight(definition.id, definition.appearance?.height ?? 2);
      if (!this.units.has(definition.id) && !('kind' in definition)) {
        this.deferredHeroes.push(definition);
      } else {
        const group = this.units.get(definition.id);
        if (group) group.position.set(definition.spawn.x, terrainHeight(next.map, definition.spawn.x, definition.spawn.z), definition.spawn.z);
        this.animators.get(definition.id)?.reset();
      }
    }
    this.queueEnemies(next.enemies);
    this.stockHeads([...this.deferredHeroes, ...next.enemies.filter(enemy => !this.units.has(enemy.id))]);
    return true;
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.previewCasts.clear();
    this.enemyPreparation?.cancel();
    this.enemyPreparation = null;
    for (const { stock } of this.headStocks.splice(0)) stock.release();
    this.enemyHeads?.release(); this.enemyHeads = null;
    this.disposeTab(); this.patrol?.dispose(); this.civilians.dispose(); this.critters?.dispose(); this.weather?.dispose(); this.traces?.dispose(); this.chart?.dispose(); this.fogOverlay.dispose(); this.markers.dispose(); this.pings.dispose(); this.stealth.dispose(); this.banners.dispose();
    this.cameraControls.dispose(); this.characterOcclusion.dispose(); this.wallsDown?.dispose();
    for (const animator of this.animators.values()) animator.dispose();
    this.selectionBox.remove(); this.effects.dispose(); this.presence.clear(); this.shadows.clear(); this.chargeTrails.dispose(); this.unwatchGraphics(); this.channels.dispose(); this.torches.dispose(); this.cantrips.dispose(); this.hoverOutline.dispose();
    this.resizeObserver.disconnect(); this.renderer.setAnimationLoop(null);
    this.renderer.domElement.removeEventListener('webglcontextlost', this.contextLost); this.renderer.domElement.removeEventListener('webglcontextrestored', this.contextRestored);
    this.summonSpares?.dispose(); this.releasePrograms(); releaseObjects(this.scene);
    // Disposing GPU resources alone leaves the canvas's context active until garbage collection.
    // Scenario switches and reconnects must release it before another renderer is created.
    this.sunset?.dispose(); this.pictureFilter?.dispose();
    if (this.sharedRenderer) this.sharedRenderer.recycle(); else { this.renderer.dispose(); this.renderer.forceContextLoss(); this.renderer.domElement.remove(); }
    for (const label of this.labels.values()) label.remove(); this.overlays.remove(); this.overlayClear?.();
  }
  setOwnHeroes(ids: HeroId[]) {
    this.ownHeroes = [...ids]; this.samples = [];
    this.chart?.setOwnHeroes(ids);
    if (ids.length) this.focusParty();
    for (const [id, label] of this.labels) {
      if (this.content.heroes.some(hero => hero.id === id)) {
      }
    }
    this.setSelection(ids);
  }
  setSelection(ids: HeroId[]) {
    this.selectedHeroes = ids.filter(id => this.ownHeroes.includes(id));
    for (const [id, ring] of this.rings) {
      const selected = this.selectedHeroes.includes(id);
      const alive = !this.latest?.heroes.find(hero => hero.id === id)?.health.dead && !this.latest?.enemies.find(enemy => enemy.id === id)?.health.dead;
      (ring.material as THREE.MeshBasicMaterial).opacity = FAINT_RING;
      ring.visible = alive && !selected;
      const glyph = this.selectionGlyphs.get(id);
      if (glyph) glyph.visible = alive && selected;
    }
  }
  private stagedLap = -2; private everStaged = false;
  /**
   * Crows stand on the road ahead of the bandit column and bolt as it nears; rabbits graze in the grass to either side.
   * Each walk-in restages them, with the crows gliding back down, so the road is never bare for a second pass.
   */
  private stageLobbyCritters() {
    const patrol = this.patrol;
    if (!patrol || !this.critters) return;
    if (patrol.lap < 0) { this.stagedLap = -2; return; } // the walk-in will start afresh when the splash shows again
    if (patrol.lap === this.stagedLap) return;
    const again = this.everStaged; this.stagedLap = patrol.lap; this.everStaged = true;
    this.critters.stage({
      crows: [[-9, .8], [-3, -1.2], [14, 1], [20, -1], [27, .6]].map(([ahead, lateral]) => patrol.spot(ahead, lateral)),
      rabbits: [[8, 13], [19, 12], [30, 10], [11, -12], [24, -11]].map(([ahead, lateral]) => patrol.spot(ahead, lateral)),
    }, again);
  }

  /** Lets the lobby patrol start walking, once the player has opened the door onto it. */
  beginSplash() { this.splashLive = true; this.focusSplash(); }
  private focusSplash() {
    this.focusCamera({ x: 0, z: 0 });
  }
  private addUnit(definition: UnitDefinition, visual = createUnitVisual(definition)) {
    this.pendingUnits.push(visual.ready);
    // A hero's summons (Shadows) answer only to their summoner's commands: they are not picked, hovered or attacked.
    if (!('summoner' in definition && definition.summoner)) this.pickTargets.push({ id: definition.id, group: visual.group, hitArea: visual.hitArea });
    this.units.set(definition.id, visual.group); this.rings.set(definition.id, visual.ring); this.labels.set(definition.id, visual.label);
    this.unitDefinitions.set(definition.id, JSON.stringify(definition));
    if (visual.selectionGlyph) this.selectionGlyphs.set(definition.id, visual.selectionGlyph);
    this.figures.set(definition.id, visual.figure);
    this.animators.set(definition.id, visual.animator);
    if (visual.torchRig) this.torches.add(definition.id, visual.torchRig);
    visual.animator.ragdollMap = this.content.map;
    if (this.content.map.terrain) visual.animator.groundHeight = (x, z) => levelHeight(this.content.map, x, z, this.unitLevels.get(definition.id));
    // Footfalls are heard near the camera and fade with distance from it.
    let paw = 0;
    visual.animator.onStep = () => { const focus = this.cameraFocus(), away = Math.hypot(visual.group.position.x - focus.x, visual.group.position.z - focus.z); if (visual.group.visible && away < 34) playStep((1 - away / 34) ** 1.5, this.surfaces.onRoad(visual.group.position.x, visual.group.position.z), definition.appearance?.rig === 'wolf', `${definition.id}:${paw++ % 4}`, this.soundPlacement(visual.group.position).pan);
      // Each footfall leaves its mark on sand, road and the tide line.
      const gait = this.gait.get(definition.id); if (visual.group.visible && gait && away < 60) { gait.foot = gait.foot === 1 ? -1 : 1; this.traces?.step(visual.group.position, gait.heading, gait.foot); } };
    this.labelHeights.set(definition.id, visual.labelHeight);
    visual.label.hidden = true;
    this.scene.add(visual.group); this.overlays.append(visual.label);
    visual.group.position.set(definition.spawn.x, levelHeight(this.content.map, definition.spawn.x, definition.spawn.z, spawnLevel(this.content.map, definition.spawn)), definition.spawn.z);
    visual.group.visible = false; // Only units present in authoritative snapshots appear.
    if (this.content.heroes.some(hero => hero.id === definition.id)) {
      const marks = new OrderMarks(definition.color);
      this.orders.set(definition.id, marks); this.scene.add(marks.mesh);
      visual.label.classList.add('hero-label'); buildHeroPlate(visual.label, (definition as HeroDefinition).classId);
    }
    if ('kind' in definition) {
      visual.label.classList.add('enemy-label');
      buildEnemyPlate(visual.label, definition as EnemyDefinition);
    }
    return visual.ready;
  }
  /** Takes a unit's figure, plate, ring and order markers out of the scene. */
  private removeUnit(id: HeroId) {
    this.figures.delete(id);
    this.unitDefinitions.delete(id);
    const group = this.units.get(id);
    this.animators.get(id)?.dispose(); this.labels.get(id)?.remove(); this.channels.remove(id); this.torches.remove(id); this.cantrips.remove(id);
    if (group) {
      this.scene.remove(group);
      releaseObjects(group);
    }
    const order = this.orders.get(id);
    if (order) { this.scene.remove(order.mesh); order.dispose(); }
    for (const map of [this.units, this.rings, this.selectionGlyphs, this.labels, this.animators, this.labelHeights, this.orders] as Map<HeroId, unknown>[]) map.delete(id);
    this.pickTargets = this.pickTargets.filter(target => target.id !== id);
  }
  /**
   * Another player brought their own character into this seat, or the hero changed equipment: rebuild its figure, plate and order markers.
   * The new figure is assembled and its shaders compiled off to the side while the old one stays in play, so a changed outfit never
   * stalls the frame (a first-seen outfit's programs took seconds to link on first draw); only the latest request for a hero swaps in.
   * Its head is meshed on a worker; assembly itself blocks for ~100 ms, so it waits out a snapshot interval too: the inventory change sent
   * right after the definition paints first.
   */
  replaceHero(definition: HeroDefinition) {
    prepareCastingVoice(definition);
    const deferred = this.deferredHeroes.findIndex(hero => hero.id === definition.id);
    if (deferred >= 0) { this.deferredHeroes[deferred] = definition; return; }
    if (!this.units.has(definition.id)) return;
    const version = (this.replacements.get(definition.id) ?? 0) + 1; this.replacements.set(definition.id, version);
    const head = stockFigureHeads([definition], FIGURE_DETAIL[graphicsOptions.characterDetail]);
    void Promise.all([head.ready, new Promise(resolve => setTimeout(resolve, 150))]).then(() => {
      if (!this.disposed && this.replacements.get(definition.id) === version) this.assembleReplacement(definition, version);
      head.release();
    });
  }
  private assembleReplacement(definition: HeroDefinition, version: number) {
    const visual = createUnitVisual(definition), discard = () => { visual.animator.dispose(); releaseObjects(visual.group); };
    void visual.ready.then(() => this.renderer.compileAsync(visual.group, this.camera, this.scene)).then(() => {
      const group = this.units.get(definition.id);
      if (this.disposed || this.replacements.get(definition.id) !== version || !group) { discard(); return; }
      const visible = group.visible, position = group.position.clone();
      this.removeUnit(definition.id);
      this.effects.setHeight(definition.id, definition.appearance?.height ?? 2);
      this.addUnit(definition, visual);
      const replacement = this.units.get(definition.id)!;
      replacement.position.copy(position); replacement.visible = visible;
    }, discard);
  }
  private replacements = new Map<HeroId, number>();
  /** Remove the previous room's projection before returning to character selection. */
  clearWorld() {
    this.channelSounds.clear(); this.monsterCasts.clear();
    this.pointer = null;
    this.selectionDrag = null; this.selectingUnits = false; this.selectionBox.hidden = true;
    this.splashLive = true;
    this.summonedIds.clear();
    this.latest = null; this.samples = []; this.sky = null; this.effects.clear(); this.markers.clear(); showPropStates(this.content, {}); this.pings.clear(); this.glancePicker.clear(); this.banners.clear(); this.presence.clear(); this.shadows.clear(); this.chargeTrails.clear();
    this.setOwnHeroes([]); this.setSelection([]); this.fogOverlay.visible = false;
    this.focusSplash();
    for (const group of this.units.values()) group.visible = false;
    for (const label of this.labels.values()) label.hidden = true;
    for (const order of this.orders.values()) order.hide();
  }
  private summonedIds = new Set<string>();
  /** Authoring preview: graphics only, without altering authoritative state. */
  previewCombatEffect(event: CombatVisualEvent, seconds = 2, targetIds: string[] = []) {
    if (!this.latest) return false;
    if (ChanneledPreviews.ability(event)) {
      const actor = this.latest.heroes.find(hero => hero.id === event.actorId && !hero.health.dead && !hero.health.dying);
      const began = !!actor && !actor.class.casting && this.previewCasts.begin(event, this.renderedGameTime, seconds, targetIds);
      if (began && event.abilityId) this.combatSounds.previewCantrip(event.abilityId, 'begin', event.from);
      return began;
    }
    this.effects.preview(event, this.latest); this.presence.cue(event, this.renderedGameTime);
    if (event.kind === 'ability' && event.abilityId) this.combatSounds.previewAbility(event.abilityId, event.to);
    // A cast that is not channeled still turns its caster toward the target and makes its gesture, as the server's windup would.
    if (event.actorId !== event.targetId && event.kind !== 'teleport' && event.kind !== 'condition') this.animators.get(event.actorId)?.play(event.kind === 'mark' ? 'mark' : 'cast', this.renderedGameTime, event.to);
    return true;
  }
  private syncSummonedEnemies(summons: EnemyDefinition[]) {
    const ids = new Set(summons.map(enemy => enemy.id));
    for (const id of this.summonedIds) if (!ids.has(id)) { this.removeUnit(id); this.content.enemies = this.content.enemies.filter(enemy => enemy.id !== id); }
    for (const enemy of summons) if (!this.summonedIds.has(enemy.id)) { if (!this.content.enemies.some(existing => existing.id === enemy.id)) this.content.enemies.push(enemy); this.effects.setHeight(enemy.id, enemy.appearance?.height ?? 1.15); if (!this.units.has(enemy.id)) void this.addUnit(enemy, this.summonSpares?.take(enemy)); }
    if (ids.size !== this.summonedIds.size || [...ids].some(id => !this.summonedIds.has(id))) this.banners.setDefinitions(this.content.enemies);
    this.summonedIds = ids;
  }
  /** Returns the snapshot as presented: blows without a windup land on their clip's contact frame (see `StrikeTiming`). */
  receive(server: WorldSnapshot): WorldSnapshot {
    const snapshot = this.strikeTiming.present(server);
    this.syncSummonedEnemies(snapshot.summonedEnemies ?? []);
    if (this.latest?.revision !== snapshot.revision) { this.samples = []; this.previewCasts.clear(); }
    this.latest = snapshot;
    this.effects.receive(snapshot); this.markers.receive(snapshot); this.presence.receive(snapshot); showPropStates(this.content, snapshot.props);
    this.fogOverlay.update(snapshot.fog); this.fogOverlay.visible = !snapshot.fog.revealed;
    const present = new Set([...snapshot.heroes, ...snapshot.enemies].map(unit => unit.id));
    // The frame loop reveals present units only when the camera or its shadows need them.
    for (const [id, group] of this.units) if (!present.has(id)) group.visible = false;
    for (const target of this.pickTargets) {
      const enemy = snapshot.enemies.find(enemy => enemy.id === target.id);
      target.hitArea.visible = !enemy?.health.dead && !enemy?.escaped;
    }
    this.animationEvents.receive(snapshot);
    for (const { event, lead } of this.strikeTiming.swings) this.animationEvents.swing(event.actorId, event.to, snapshot.gameTime, lead);
    this.combatSounds.receive(snapshot); this.monsterCasts.receive(snapshot);
    this.samples.push({ at: performance.now(), snapshot });
    if (this.samples.length > 12) this.samples.shift();
    for (const hero of [...snapshot.heroes, ...snapshot.enemies]) {
      const isHero = 'connected' in hero;
      if ('kind' in hero) {
        const label = this.labels.get(hero.id); if (!label) continue;
        if (hero.health.dead) label.hidden = true;
        else updateEnemyPlate(label, hero, snapshot.inCombat);
      }
      else updateHeroPlate(this.labels.get(hero.id)!, hero as HeroState, this.ownHeroes.includes(hero.id), snapshot.inCombat);
      // A disconnected hero fades; a hidden one sinks into shadow instead (`ShadowFigures`).
      const group = this.units.get(hero.id); if (!group) continue;
      const opacity = hero.health.dead || !isHero || hero.connected ? 1 : .45;
      // A faded hero's surfaces are its own; every other figure keeps drawing with the shared ones (figure-materials.ts).
      if (this.unitOpacity.get(group) !== opacity) { if (opacity < 1) ownFigureMaterials(group); else shareFigureMaterials(group); }
      if (this.unitOpacity.get(group) !== opacity) group.traverse(child => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
          if (child.material.transparent !== (opacity < 1)) { child.material.transparent = opacity < 1; child.material.needsUpdate = true; }
          child.material.opacity = opacity;
        }
      });
      this.unitOpacity.set(group, opacity);
      const selected = this.selectedHeroes.includes(hero.id);
      this.rings.get(hero.id)!.visible = !hero.health.dead && !selected;
      const glyph = this.selectionGlyphs.get(hero.id);
      if (glyph) glyph.visible = !hero.health.dead && selected;
      const targeted = snapshot.heroes.some(h => this.selectedHeroes.includes(h.id) && h.combat.targetId === hero.id);
      const quarry = snapshot.heroes.some(h => this.ownHeroes.includes(h.id) && h.class.quarryId === hero.id);
      const ringMaterial = this.rings.get(hero.id)!.material as THREE.MeshBasicMaterial;
      const dying = isHero && (hero as HeroState).health.dying;
      // A Dying hero's circle shows at full strength, so its heartbeat reads.
      ringMaterial.opacity = targeted || quarry || dying || this.selectedHeroes.includes(hero.id) ? 1 : FAINT_RING;
      ringMaterial.color.setHex(targeted ? 0xff4545 : quarry ? 0xffa24d : dying ? 0xa8321f : [...this.content.heroes, ...this.content.enemies].find(unit => unit.id === hero.id)!.color);
      // The circle is the unit's health gauge: a hero's drains with its hit points, an enemy's by condition tier.
      const fill = isHero ? (dying ? 0 : hero.health.hp / Math.max(1, hero.health.maxHp)) : CONDITION_FILL[conditionOf(hero.health)];
      setGaugeFill(ringMaterial, fill);
      if (isHero) { styleHeroGauge(ringMaterial, fill, dying, snapshot.inCombat); if (glyph) styleHeroGauge(glyph.material as THREE.Material, fill, dying, snapshot.inCombat); }
      if (glyph) { const glyphMaterial = glyph.material as THREE.MeshBasicMaterial; setGaugeFill(glyphMaterial, fill); glyphMaterial.color.setHex(dying ? 0xa8321f : this.content.heroes.find(unit => unit.id === hero.id)!.color); }
    }
    return snapshot;
  }
  /** Development only: the sky the splash shows while there is no world and so no server clock. `null` returns to the default dusk. */
  previewClock: number | null = null;
  /** The server clock, eased so a rest sweeps the sun across the sky instead of snapping it, while ordinary play follows the clock closely. */
  private skyClock(now: number) {
    const target = this.latest?.clock ?? this.previewClock ?? CLOCK_START_MINUTES, elapsed = Math.min(.25, (now - this.skyAt) / 1000); this.skyAt = now;
    if (this.sky === null || target < this.sky) this.sky = target; // first frame, or a restart rewound the clock
    else this.sky += (target - this.sky) * (1 - Math.exp(-elapsed * 3));
    return this.sky;
  }
  private resize() {
    const { clientWidth: width, clientHeight: height } = this.host;
    if (!width || !height) return; // Hidden or not laid out yet: keep the last real size; the observer fires again when it has one.
    this.viewport = { width, height };
    resizeIsometricCamera(this.camera, width, height);
    if (this.splashLive && !this.latest && !this.ownHeroes.length) this.focusSplash();
    this.renderer.setSize(width, height);
    this.hoverOutline.resize(width, height);
  }
  private canTargetEnemy(id: string) {
    return this.enemyTargetingAllowed() && this.units.get(id)?.visible === true && isEnemyClickTarget(id, this.latest, this.selectedHeroes);
  }
  /** Both release and preview use these exact body-proxy/ownership/death rules. */
  private rectangleHeroes(start: { x: number; y: number }, end: { x: number; y: number }, rect = this.renderer.domElement.getBoundingClientRect()) {
    const covered = new Set(selectUnitsInRectangle(this.pickTargets.filter(target => this.ownHeroes.includes(target.id)), this.camera, rect, start, end));
    return this.ownHeroes.filter(id => covered.has(id) && !this.latest?.heroes.find(hero => hero.id === id)?.health.dead);
  }
  private selectionPreviewFigures(rect: DOMRect, figures: THREE.Object3D[]) {
    const drag = this.selectionDrag;
    figures.length = 0;
    if (!drag || this.selectionBox.hidden) return;
    const covered = this.rectangleHeroes(drag.start, drag.end, rect);
    for (const id of this.ownHeroes) {
      if ((!covered.includes(id) && !(drag.additive && this.selectedHeroes.includes(id))) || !this.units.get(id)?.visible
        || this.latest?.heroes.find(hero => hero.id === id)?.health.dead) continue;
      const figure = this.figures.get(id);
      if (figure) figures.push(figure);
    }
  }
  private canTargetAlly(id: string) {
    return this.allyTargetingAllowed(id) && this.units.get(id)?.visible === true && isAllyClickTarget(id, this.latest);
  }
  private hoverScreen(): THREE.Vector2 | undefined {
    if (this.selectingUnits || this.cameraControls.dragging || !this.pointer) return;
    const canvas = this.renderer.domElement, box = canvas.getBoundingClientRect();
    if (document.elementFromPoint(box.left + this.pointer.x, box.top + this.pointer.y) !== canvas) return;
    if (!box.width || !box.height) return;
    return this.pointerNdc.set(this.pointer.x / box.width * 2 - 1, -this.pointer.y / box.height * 2 + 1);
  }
  /** The unit under the cursor; `color`, its outline's, is set only when a click would select or target it. */
  private hoveredUnit(screen: THREE.Vector2 | undefined): { id: string; figure: THREE.Object3D; color?: number } | undefined {
    if (!screen) return;
    this.raycaster.setFromCamera(screen, this.camera);
    const id = pickUnit(this.raycaster, this.pickTargets);
    if (id === undefined) return;
    const figure = this.figures.get(id);
    if (!figure) return;
    if (this.canTargetEnemy(id)) return { id, figure, color: 0xff3535 };
    if (this.canTargetAlly(id)) return { id, figure, color: 0x35e875 };
    return { id, figure };
  }
  private platesHeld = false;
  private readonly calledPlateIds = new Set<string>();
  /** Units whose plates are called up this frame, besides every one while Tab is held. */
  private calledPlates(hovered: string | undefined) {
    const called = this.calledPlateIds; called.clear();
    if (hovered) called.add(hovered);
    if (this.menuFocus) called.add(this.menuFocus.id);
    for (const id of this.targetHints) called.add(id);
    for (const hero of this.latest?.heroes ?? []) if (this.selectedHeroes.includes(hero.id) && hero.combat.targetId) called.add(hero.combat.targetId);
    return called;
  }
  private render() {
    if (!this.prepared || this.disposed) return;
    const now = performance.now();
    if (this.frameStatsSink) this.renderer.info.reset();
    this.frameObserver?.begin(now);
    const frame = Math.min(.1, (now - this.lastFrame) / 1000);
    // Read the displayed DOM before this frame moves plates or writes overlay styles.
    // Resolve world picking later, against the updated camera and interpolated units.
    const hoverScreen = this.hoverScreen();
    const selectionRect = this.selectionDrag ? this.renderer.domElement.getBoundingClientRect() : undefined;
    tickGauges(now / 1000, frame);
    for (const glyph of this.selectionGlyphs.values()) {
      if (!glyph.visible) continue;
      glyph.rotation.z = (glyph.rotation.z + frame * Math.PI / 40) % (Math.PI * 2);
      // The glyph turns, so its gauge's front is turned back to keep facing the camera.
      const gauge = healthGauge(glyph.material as THREE.Material); if (gauge) gauge.front.value = GAUGE_FRONT - glyph.rotation.z;
    }
    this.cameraControls.update((now - this.lastFrame) / 1000); this.lastFrame = now;
    // Panning keeps the camera's height: it does not rise and fall over the ground, so cliffs and hills stay put under the cursor.
    if (fitIsometricDepth(this.camera)) this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld(true);
    if (!this.looseView && this.camera.zoom >= CAMERA_ZOOM_RANGE.min) this.keepViewInsideMap();
    else {
      // Development zoom goes out past the whole map: only the centre of the view is kept on it.
      const focus = this.cameraFocus();
      const bounded = { x: THREE.MathUtils.clamp(focus.x, -this.content.map.width / 2, this.content.map.width / 2), z: THREE.MathUtils.clamp(focus.z, -this.content.map.depth / 2, this.content.map.depth / 2) };
      if (focus.x !== bounded.x || focus.z !== bounded.z) this.focusCamera(bounded, this.cameraGroundHeight);
    }
    // Before a party has a world to show, a bandit patrol keeps the road behind the lobby alive. It is built on first need.
    const idle = this.splashLive && !this.latest && !this.ownHeroes.length;
    if (idle && !this.patrol) this.patrol = new LobbyPatrol(this.scene, this.content.map);
    this.patrol?.update(now / 1000, idle, (position, height) => this.unitView.visible(position, height));
    // Night, mist, rain and wind follow the server's clock; the weather then dims and thickens what the time of day has lit.
    const clock = this.skyClock(now);
    setConditions(clock, this.content.map.scenery?.seed ?? 0, this.forcedWeather);
    this.sunset?.update(this.camera, clock);
    this.weather?.update(this.camera, this.sunset, this.cameraFocus(), now / 1000); this.sunset?.fit(this.camera);
    this.unitView.update(this.camera, this.renderer.shadowMap.enabled ? this.sunset?.lights.sun : undefined);
    this.fogOverlay.tick(now / 1000);
    const at = now - 100;
    this.frameObserver?.phase('environment');
    while (this.samples.length > 2 && this.samples[1].at <= at) this.samples.shift();
    const first = this.samples[0], second = this.samples[1] || first;
    if (first) {
      const alpha = second.at === first.at ? 1 : THREE.MathUtils.clamp((at - first.at) / (second.at - first.at), 0, 1);
      // x + (y - x) * t is exact when paused (y === x); MathUtils.lerp rounds below x, which the animator reads as a restart.
      const gameTime = first.snapshot.gameTime + (second.snapshot.gameTime - first.snapshot.gameTime) * alpha;
      this.renderedGameTime = gameTime;
      this.mapVisual.update(gameTime, this.viewTest(), this.cameraFocus());
      this.civilians.update(gameTime, second.snapshot.fog, false, (position, height) => this.unitView.visible(position, height));
      this.critters?.update(gameTime, { party: second.snapshot.fog.sources, fog: second.snapshot.fog, night: conditions.night, active: true });
      const states = this.frameStates; states.length = 0;
      states.push(...second.snapshot.heroes, ...second.snapshot.enemies);
      const earlier = this.earlierStates; earlier.length = 0;
      earlier.push(...first.snapshot.heroes, ...first.snapshot.enemies);
      // Every unit is placed before any is animated, so a pair each reach for where the other stands this frame, not last frame.
      for (const target of states) {
        const h = earlier.find(unit => unit.id === target.id) ?? target; // units arriving from the fog have no earlier sample
        const unit = this.units.get(h.id);
        if (!unit) continue; // a snapshot from a room this scene was not built for (a restarted scenario server) carries units it has no figure for
        // Exact for a unit that has not moved (see gameTime above), so still bodies keep exactly the same place frame after frame.
        const x = h.x + (target.x - h.x) * alpha, z = h.z + (target.z - h.z) * alpha;
        // A unit's level changes only on open ground, where every level's ground is the same.
        const level = target.level ?? 0, ground = levelMap(this.content.map, level);
        this.unitLevels.set(h.id, level);
        unit.position.set(x, terrainHeight(ground, x, z), z);
        unit.visible = this.unitView.visible(unit.position, this.animators.get(h.id)!.rig.height, h.radius);
        // While a roof is cut away for the heroes inside, whoever stands on its top is not drawn floating over them.
        if (unit.visible && !level && this.caves && this.caves.cut > .5 && this.caves.under(x, z)) unit.visible = false;
        if (!unit.visible) continue;
        const ring = this.rings.get(h.id), glyph = this.selectionGlyphs.get(h.id);
        if (ring) conformTerrainDecal(ring, ground);
        if (glyph) conformTerrainDecal(glyph, ground);
        this.trackGait(h.id, unit.position);
      }
      this.frameObserver?.phase('world');
      // What each head glances at, from the snapshot and the room's pings, so every screen turns the same heads.
      const glanceUnits = this.glanceUnits; glanceUnits.length = 0;
      for (const state of states) { const unit = this.units.get(state.id); if (unit) glanceUnits.push({ state, x: unit.position.x, y: unit.position.y, z: unit.position.z, height: this.animators.get(state.id)!.rig.height }); }
      const glances = this.glancePicker.pick(glanceUnits, this.pings.marks().map(mark => ({ at: { ...mark.at, y: this.groundHeight(mark.at.x, mark.at.z) }, age: mark.age })), gameTime, second.snapshot.inCombat);
      if (this.frameObserver?.animation !== false) for (const target of states) {
        const unit = this.units.get(target.id)!, mate = target.pair ? this.units.get(target.pair.partnerId) : undefined, mateAnimator = target.pair ? this.animators.get(target.pair.partnerId) : undefined;
        const animator = this.animators.get(target.id)!;
        // Hide's creep: low and slow while the stance holds.
        if ('sneaking' in animator) animator.sneaking = target.combat.hiding === true && !target.health.dying;
        animator.glance = glances.get(target.id);
        // Physical falls must finish and release their worlds even outside the rendered view.
        if (!needsUnitPose(unit.visible, target.health.dead, animator)) { this.posedAt.delete(target.id); continue; }
        // Out of combat, standing still and alive, a figure only idles: above IDLE_POSE_RATE it is re-posed on alternate frames.
        const idle = !second.snapshot.inCombat && !target.route.length && !target.pair && !target.health.dead && !target.health.dying && animator.deathState === 'alive'
          && ((animator as { proneState?: string }).proneState ?? 'standing') === 'standing' && !animator.playing();
        const posedAt = this.posedAt.get(target.id);
        if (idle && posedAt !== undefined && gameTime >= posedAt && gameTime - posedAt < 1 / IDLE_POSE_RATE) continue;
        this.posedAt.set(target.id, gameTime);
        // A cast's progress is read off its remaining time, which arrives in steps of a snapshot; smooth it, or it moves at the snapshot rate.
        const shown = this.monsterCasts.project(this.previewCasts.project(smoothCast(earlier.find(unit => unit.id === target.id) ?? target, target, alpha), gameTime), gameTime);
        const combatReady = isCombatPoseActive(shown, second.snapshot.inCombat);
        const castTargetId = (shown as Partial<HeroState>).class?.casting?.targetId ?? target.combat.targetId;
        // A foe glancing toward a noise turns to it, as the server's sight does (`viewHeading`).
        this.animators.get(target.id)!.update(shown, unit.position, gameTime, (target as EnemyState).lookAt ?? states.find(state => state.id === castTargetId), combatReady,
          mate && mateAnimator ? { x: mate.position.x, z: mate.position.z, height: mateAnimator.rig.height, body: mateAnimator.body, mouth: mateAnimator.mouthShare } : undefined);
      }
      this.torches.update(gameTime, now / 1000, id => {
        const state = states.find(unit => unit.id === id);
        return state && { visible: this.units.get(id)?.visible ?? false, alive: !state.health.dead, underground: standingLevel(this.content.map, state) > 0 };
      }, this.cameraFocus());
      this.frameObserver?.phase('animation');
      // A selected hero holding Hide in a fight sees which ground the foes can see, and where its route steps into their sight.
      const sneaking = second.snapshot.inCombat ? second.snapshot.heroes.find(hero => this.selectedHeroes.includes(hero.id) && hero.combat.hiding && !hero.health.dead) : undefined;
      this.stealth.update(states, second.snapshot.enemies, sneaking, this.units, performance.now() / 1000);
      const previewing = this.orientPreview(second.snapshot.heroes), turned = this.orienting?.facing;
      for (const hero of second.snapshot.heroes) {
        // The route under way and the queued moves after it, for selected heroes only; a formation being turned shows each hero's slot instead.
        const order = this.orders.get(hero.id)!, queued = hero.queue?.flatMap(next => next.kind === 'move' ? [next.target] : []) ?? [];
        const exposed = hero.combat.hiding && second.snapshot.inCombat ? (point: Point) => this.stealth.exposed({ ...point, ...hero.level ? { level: hero.level } : {} }) : undefined;
        // A follower's route ends on its ally, wherever the ally is drawn now, so the ring stays locked on it.
        const ally = hero.followId ? this.units.get(hero.followId)?.position : undefined;
        const route = ally ? [...hero.route.slice(0, -1), { x: ally.x, z: ally.z }] : hero.route;
        const slot = previewing.get(hero.id);
        if (slot) order.drawSlot(this.content.map, slot.place, turned ?? slot.facing, this.orienting?.anchor.level);
        else if (this.selectedHeroes.includes(hero.id) && (route.length || queued.length)) order.draw(this.content.map, this.units.get(hero.id)!.position, route, queued, undefined, exposed, ally ? undefined : hero.standFacing, hero.level);
        else order.hide();
      }
      this.banners.update(second.snapshot, this.units, this.labelHeights, this.camera, this.viewport.height, frame);
      const spellScale = pixelsPerMeter(this.camera, this.renderer.domElement.height, this.camera.position);
      this.shadows.update(new Set(second.snapshot.heroes.filter(hero => hero.hidden && hero.connected && !hero.health.dead).map(hero => hero.id)), this.units, gameTime);
      this.presence.update(gameTime, this.figures); this.chargeTrails.update(gameTime, states, this.units, spellScale);
      const previewCasts = this.previewCasts.casts(gameTime);
      if (this.frameObserver?.effects !== false) {
        this.previewCasts.release(gameTime, this.animators, this.units, (event, origin) => {
          const target = states.find(state => state.id === event.targetId);
          if (target && !target.health.dead) { this.effects.preview({ ...event, to: { x: target.x, z: target.z } }, second.snapshot, origin, gameTime); if (event.abilityId) this.combatSounds.previewCantrip(event.abilityId, 'release', origin); }
        });
        this.effects.updateFrame(second.snapshot, first.snapshot, alpha, this.units, spellScale, this.animators, previewCasts);
        this.channels.update(this.animators, id => this.units.get(id)?.visible ?? false, now, spellScale, this.effects.spellLights);
        this.cantrips.update(this.animators, id => this.units.get(id)?.visible ?? false, gameTime, spellScale);
      }
    }
    else {
      this.mapVisual.update(now / 1000, this.viewTest(), this.cameraFocus());
      this.civilians.update(now / 1000, undefined, this.splashLive, (position, height) => this.unitView.visible(position, height));
      this.stageLobbyCritters();
      this.critters?.update(now / 1000, { party: [], threats: this.patrol?.crew() ?? [], night: conditions.night, active: this.splashLive });
    }
    const hovered = this.hoveredUnit(hoverScreen);
    this.hoveredId = hovered?.id ?? null;
    const calledPlates = this.calledPlates(hovered?.id);
    for (const [id, hero] of this.units) {
      const label = this.labels.get(id)!;
      if (!hero.visible || this.latest?.enemies.some(enemy => enemy.id === id && enemy.health.dead)) { label.hidden = true; continue; }
      // Plates come up only on demand: under the cursor, for what the party is fighting or about to target, the open menu's unit, or all while Tab is held.
      label.classList.toggle('called', this.platesHeld || calledPlates.has(id));
      const position = this.labelProjection.set(hero.position.x, hero.position.y + this.labelHeights.get(id)!, hero.position.z).project(this.camera);
      label.hidden = position.z < -1 || position.z > 1 || Math.abs(position.x) > 1.05 || Math.abs(position.y) > 1.05;
      if (!label.hidden && this.content.heroes.some(unit => unit.id === id)) {
        // Hero plates cannot be clicked, so they step aside instead when the cursor is near, rather than hiding what is behind them.
        const x = (position.x + 1) / 2 * this.viewport.width, y = (-position.y + 1) / 2 * this.viewport.height;
        label.classList.toggle('faded', !!this.pointer && Math.abs(this.pointer.x - x) < 44 && Math.abs(this.pointer.y - y) < 22);
      }
      // A HUD menu open over the world keeps plates out of its ring; its own target keeps its plate.
      const menu = this.menuFocus;
      if (!label.hidden && menu && id !== menu.id && Math.hypot((position.x + 1) / 2 * this.viewport.width - menu.x, (-position.y + 1) / 2 * this.viewport.height - menu.y) < menu.radius) label.hidden = true;
      if (!label.hidden) label.style.transform = `translate(-50%, -50%) translate(${(position.x + 1) / 2 * this.viewport.width}px, ${(-position.y + 1) / 2 * this.viewport.height}px)`;
    }
    this.traces?.update(now / 1000);
    const tramplers = this.tramplers; tramplers.length = 0;
    for (const [id, group] of this.units) if (group.visible) {
      let walker = tramplers[tramplers.length];
      if (!walker) { walker = { id, x: 0, z: 0 }; tramplers.push(walker); }
      walker.id = id; walker.x = group.position.x; walker.z = group.position.z;
    }
    this.trample.update(frame, tramplers, this.cameraFocus());
    this.pings.update(frame);
    this.chart?.update(now, this.latest, this.cameraFocus(), this.cameraGroundView(), this.pings.marks());
    if (this.frameObserver?.effects !== false) this.effects.render(this.camera, this.viewport.width, this.viewport.height);
    this.markers.render(this.camera, now, this.viewport);
    this.effects.shake.apply(this.camera);
    this.mapVisual.cull?.(this.camera, this.renderer.shadowMap.enabled ? this.sunset?.lights.sun ?? this.plainSun : null);
    const cutouts = this.cutoutHeroes;
    let cutoutCount = 0;
    for (const hero of this.latest?.heroes ?? []) {
      const unit = this.units.get(hero.id), figure = this.figures.get(hero.id), animator = this.animators.get(hero.id);
      if (!unit?.visible || !figure?.visible || !animator || hero.health.dead) continue;
      const cutout = cutouts[cutoutCount] ??= { position: unit.position, height: 0, visible: true };
      cutout.position = unit.position; cutout.height = animator.rig.height;
      cutoutCount++;
    }
    cutouts.length = cutoutCount;
    // Inside an interior the rock sinks round the selected heroes (or the whole party when none is selected).
    const insiders = this.wallsDownHeroes; insiders.length = 0;
    for (const id of this.selectedHeroes) { const unit = this.units.get(id); if (unit?.visible) insiders.push(unit.position); }
    if (!insiders.length) for (const cutout of cutouts) insiders.push(cutout.position);
    this.wallsDown.update(frame, insiders);
    this.caves?.update(frame, insiders.some(position => this.wallsDown.inside(position.x, position.z, position.y)));
    this.characterOcclusion.update(this.camera, cutouts);
    const previewing = this.selectionDrag !== null && !this.selectionBox.hidden;
    const outlineFigures = this.outlineFigures;
    outlineFigures.length = 0;
    if (previewing && selectionRect) this.selectionPreviewFigures(selectionRect, outlineFigures);
    else if (this.targetHints.length) for (const id of this.targetHints) { const figure = this.figures.get(id); if (figure) outlineFigures.push(figure); }
    else if (this.menuFocus && this.figures.get(this.menuFocus.id)) outlineFigures.push(this.figures.get(this.menuFocus.id)!);
    else if (hovered?.color !== undefined) outlineFigures.push(hovered.figure);
    this.frameObserver?.phase('effectsAndOverlays');
    // Bodies standing on sunk rock (units, critters) are drawn on its new top for this frame only.
    if (this.wallsDown.active) this.wallsDown.lower(this.scene.children.filter(child => !this.mapRoots.has(child)));
    this.frameObserver?.beforeDraw();
    // Picture filters (development only for now) draw the world into their own target; off, the world goes straight to the canvas.
    const filter = pictureFiltersActive() ? this.pictureFilter ??= new PictureFilterPass(this.renderer) : null;
    // The 3D HUD layer can go into the filtered picture (the `filtered` mode) instead of over it.
    const groundCrisp = !!filter && PICTURE_FILTERS.groundUi === 'crisp' && this.frameObserver?.rendering !== false;
    const hudInside = !!filter && PICTURE_FILTERS.hud === 'filtered' && this.frameObserver?.rendering !== false;
    // The sun's shadow map is redrawn at most SHADOW_RATE times a second: above it, alternate frames reuse the last map with the
    // matrices it was drawn with, so still shadows stay exact and only a moving figure's shadow trails by a frame.
    const shadowMap = this.renderer.shadowMap, shadowDue = now - this.shadowDrawnAt >= 1000 / SHADOW_RATE || now < this.shadowDrawnAt;
    if (shadowMap.autoUpdate && !shadowDue) shadowMap.autoUpdate = false;
    else if (shadowMap.autoUpdate) this.shadowDrawnAt = now;
    if (this.frameObserver?.rendering !== false) {
      filter?.begin(this.camera);
      if (groundCrisp) this.camera.layers.disable(GROUND_UI_LAYER);
      this.hoverOutline.render(this.frameObserver?.outlines === false ? [] : outlineFigures, previewing ? 0x35e875 : this.targetHints.length ? 0xffb35c : this.menuFocus ? (this.latest?.enemies.some(enemy => enemy.id === this.menuFocus!.id) ? 0xff3535 : 0x35e875) : hovered?.color,
        () => {
          this.characterOcclusion.renderEdges(this.renderer, this.scene, this.camera);
          if (filter) {
            if (hudInside) this.drawOverlay(true);
            filter.finish();
            if (groundCrisp) this.drawGroundUi();
          }
        });
    }
    if (this.frameObserver?.rendering !== false && !hudInside) this.drawOverlay();
    this.wallsDown.restore();
    shadowMap.autoUpdate = true;
    this.frameObserver?.phase('renderSubmission');
    this.effects.shake.restore(this.camera);
    this.frameObserver?.end();
    if (this.onFrameStats) {
      const info = this.renderer.info.render;
      this.onFrameStats({ cpuMs: performance.now() - now, calls: info.calls, triangles: info.triangles, programs: this.renderer.info.programs?.length ?? 0 });
    }
  }
  /**
   * Development tools (the scenario viewer's frame stats): told once a frame how long the frame's work took and what it drew. While it
   * is set, draw counts add up over every pass of a frame instead of resetting at each one.
   */
  set onFrameStats(sink: ((stats: { cpuMs: number; calls: number; triangles: number; programs: number }) => void) | null) {
    this.frameStatsSink = sink; this.renderer.info.autoReset = !sink;
  }
  get onFrameStats() { return this.frameStatsSink; }
  private frameStatsSink: ((stats: { cpuMs: number; calls: number; triangles: number; programs: number }) => void) | null = null;
  /** Development tools: distance fog on (the default) or off. Lit surfaces rebuild their shaders once for the change. */
  setDistanceFog(on: boolean) { if (!on && this.scene.fog) this.distanceFog = this.scene.fog; else if (on && this.distanceFog) this.scene.fog = this.distanceFog; if (!on) this.scene.fog = null; }
  private distanceFog: THREE.Fog | THREE.FogExp2 | null = null;
  get distanceFogOn() { return this.scene.fog !== null; }
  /** Draws the ground UI layer sharp onto the canvas after the filter pass, which has left the world's depth there so figures and props still cover it. */
  private drawGroundUi() {
    const { renderer, camera } = this, shadow = renderer.shadowMap, autoClear = renderer.autoClear, shadowUpdate = shadow.autoUpdate, list = this.groundUiObjects;
    // A small scene borrowing just the ground UI, so the draw does not walk the whole world again. An object counts only while every ancestor shows.
    list.length = 0;
    const take = (object: THREE.Object3D | undefined) => {
      if (!object?.visible) return;
      for (let parent = object.parent; parent; parent = parent.parent) if (!parent.visible) return;
      list.push(object);
    };
    for (const ring of this.rings.values()) take(ring);
    for (const glyph of this.selectionGlyphs.values()) take(glyph);
    for (const order of this.orders.values()) take(order.mesh);
    this.pings.groundUi(take);
    this.groundUiScene.children = list; this.groundUiScene.fog = this.scene.fog;
    camera.layers.set(GROUND_UI_LAYER);
    try { renderer.autoClear = false; shadow.autoUpdate = false; renderer.render(this.groundUiScene, camera); }
    finally { renderer.autoClear = autoClear; shadow.autoUpdate = shadowUpdate; camera.layers.enable(0); camera.layers.enable(GROUND_UI_LAYER); }
  }
  private groundUiScene = Object.assign(new THREE.Scene(), { matrixWorldAutoUpdate: false });
  private groundUiObjects: THREE.Object3D[] = [];
  private labelProjection = new THREE.Vector3();
  private frustum = new THREE.Frustum(); private frustumMatrix = new THREE.Matrix4(); private frustumSphere = new THREE.Sphere();
  /** World circles (ground plane, generous height) against the camera frustum, for skipping off-screen animation work. */
  private viewTest() {
    this.camera.updateMatrixWorld();
    this.frustum.setFromProjectionMatrix(this.frustumMatrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse));
    return (x: number, z: number, radius: number) => { this.frustumSphere.set(this.frustumSphere.center.set(x, 8, z), radius + 12); return this.frustum.intersectsSphere(this.frustumSphere); };
  }
  private cameraFocus(): Point {
    const direction = this.camera.getWorldDirection(new THREE.Vector3());
    const center = this.camera.position.clone().addScaledVector(direction, (this.cameraGroundHeight - this.camera.position.y) / direction.y);
    return { x: center.x, z: center.z };
  }
  /** Centres the view on a point; a jump (to the party, a chart pick) also settles the camera at that point's ground height. */
  /** In play the camera stops before the edge of the map comes into view: every corner of the view stays over the map. */
  private keepViewInsideMap() {
    const corners = this.cameraGroundView(), half = { x: this.content.map.width / 2, z: this.content.map.depth / 2 };
    const shift = (axis: 'x' | 'z') => {
      const low = Math.min(...corners.map(corner => corner[axis])), high = Math.max(...corners.map(corner => corner[axis]));
      if (high - low > half[axis] * 2) return -(low + high) / 2;
      return low < -half[axis] ? -half[axis] - low : high > half[axis] ? half[axis] - high : 0;
    };
    const x = shift('x'), z = shift('z');
    if (!x && !z) return;
    this.camera.position.x += x; this.camera.position.z += z; this.camera.updateMatrixWorld(true);
  }
  private focusCamera(point: Point, height = this.groundHeight(point.x, point.z)) {
    let current = this.cameraFocus();
    if (!Number.isFinite(current.x) || !Number.isFinite(current.z)) { // A NaN camera never recovers by moving; start again from the default view.
      this.camera.position.set(26, this.cameraGroundHeight + 26, 26); this.camera.lookAt(0, this.cameraGroundHeight, 0); this.camera.updateMatrixWorld(true); current = this.cameraFocus();
    }
    this.camera.position.x += point.x - current.x; this.camera.position.z += point.z - current.z;
    this.camera.position.y += height - this.cameraGroundHeight; this.cameraGroundHeight = height;
    this.camera.updateMatrixWorld(true);
  }
  /** Development tools: centre the camera on a map point, optionally setting its zoom (within the wheel's range). */
  /** Notes which way a unit is walking, from where it was a moment ago, for its footprints. */
  private trackGait(id: string, at: THREE.Vector3) {
    const gait = this.gait.get(id);
    if (!gait) { this.gait.set(id, { x: at.x, z: at.z, heading: 0, foot: 1 }); return; }
    const dx = at.x - gait.x, dz = at.z - gait.z;
    if (dx * dx + dz * dz > 1e-4) gait.heading = Math.atan2(dx, dz);
    gait.x = at.x; gait.z = at.z;
  }

  /** Forces one kind of weather whatever the clock says, or `null` to follow the clock again. For studies and scenarios. */
  setWeather(preset: WeatherPreset | null) { this.forcedWeather = preset; }
  /** Development tools: sets the wheel's and `lookAt`'s zoom range, and keeps only the centre of the view on the map at any zoom. */
  setZoomRange(range: { min: number; max: number }) { this.cameraControls.zoomRange = range; this.looseView = true; }
  lookAt(point: Point, zoom?: number) {
    this.focusCamera(point);
    if (zoom !== undefined) { this.camera.zoom = THREE.MathUtils.clamp(zoom, this.cameraControls.zoomRange.min, this.cameraControls.zoomRange.max); this.camera.updateProjectionMatrix(); }
  }
  /** Where a unit's voice is heard from: louder the nearer it is to the camera's focus, and panned to the side of the screen it stands on. Null when it isn't drawn. */
  /** The unit (hero or enemy) under a point in client pixels, or null. */
  unitAt(clientX: number, clientY: number): string | null {
    const box = this.renderer.domElement.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    this.raycaster.setFromCamera(new THREE.Vector2((clientX - box.left) / box.width * 2 - 1, -(clientY - box.top) / box.height * 2 + 1), this.camera);
    return pickUnit(this.raycaster, this.pickTargets) ?? null;
  }
  /**
   * A HUD menu open on a unit: the unit keeps the hover outline whatever the pointer is over, and other units' plates within `radius`
   * CSS pixels of the screen point are hidden so they never sit on the menu. Null clears it.
   */
  setMenuFocus(focus: { id: string; x: number; y: number; radius: number } | null) { this.menuFocus = focus; }
  /**
   * Screen boxes (CSS pixels from the canvas's top left) the HUD's 3D pieces cover. The world overlay layer is clipped around them, so
   * a plate or callout passing a piece goes behind it (the HUD's own canvas sits over the page; the filtered mode draws it into this one).
   */
  setHudBlocks(blocks: readonly ScreenBox[]) {
    const clip = hudClipPath(blocks, this.viewport.width, this.viewport.height);
    if (clip !== this.hudClip) { this.hudClip = clip; this.overlays.style.clipPath = clip; }
  }
  private hudClip = '';
  private menuFocus: { id: string; x: number; y: number; radius: number } | null = null;
  /** Units to outline as possible targets (a multi-target power's other choices); empty clears them. */
  setTargetHints(ids: readonly string[]) { this.targetHints = ids; }
  private targetHints: readonly string[] = [];
  /** True while the camera is being dragged. */
  get cameraDragging() { return this.cameraControls.dragging; }
  /**
   * Draws the HUD layer after the frame, given this scene's renderer: `inside` is the filtered mode, where it goes into this frame;
   * otherwise it draws where it likes (its own canvas). Profiles time it on its own (`hud`, inside `renderSubmission`) and can leave it out.
   */
  private drawOverlay(inside = false) {
    if (!this.overlay || this.frameObserver?.hud === false) return;
    const started = this.frameObserver?.measure ? performance.now() : 0;
    this.overlay(this.renderer, this.viewport.width, this.viewport.height, inside);
    if (started) this.frameObserver?.measure?.('hud', performance.now() - started);
  }
  /** `warm` links the overlay's shaders while the world prepares (once per scene), when it is a scene of its own. */
  /** A screen-space layer drawn after the world each frame (the 3D HUD); null removes it. Sizes are CSS pixels. `clear` wipes what it drew when this scene goes. */
  setOverlay(draw: HudOverlay | null, warm?: (renderer: THREE.WebGLRenderer, active: () => boolean) => Promise<void>, clear?: () => void) { this.overlay = draw; this.overlayWarm = warm ?? null; this.overlayClear = clear ?? null; }
  private overlayClear: (() => void) | null = null;
  private overlayWarm: ((renderer: THREE.WebGLRenderer, active: () => boolean) => Promise<void>) | null = null;
  private overlayWarmed = false;
  private pictureFilter: PictureFilterPass | null = null;
  /**
   * The GPU context can go at any time (driver reset, the browser reclaiming the oldest context). Three restores its own state when it
   * returns; the scene stops drawing meanwhile, then links every shader again behind the page's message.
   * `handlers` only report: the recovery happens with or without them.
   */
  contextHandlers: { lost(): void; restored(rebuilt: Promise<void>): void; progress(message: string, fraction: number): void } | null = null;
  /**
   * A new number of spell or torch lights, or shadows turned on or off, changes every lit shader. Drawn on the next frame, the programs link
   * one after another inside it (20 s and more on D3D, the coast's battle); instead the frame loop stops and the scene prepares again, which
   * links them in parallel. `handlers` show the wait (the loading overlay); the relink happens with or without them.
   */
  lightingHandlers: { progress(message: string, fraction: number): void; done(): void } | null = null;
  /** Lighting changed while the scene was preparing: the warming already under way compiled the old lights, so it runs once more. */
  private lightingChanged = false;
  /** Whether the last preparation was the lobby's, which a relink repeats. */
  private preparedLobby = false;
  /** Lighting every lit shader has been linked for on this context (`program-retention.ts` keeps them): switching back to one needs no wait. */
  private warmedLighting = new Set<string>();
  private lightingKey() { return [this.channels.lightCount, this.torches.lightCount, this.renderer.shadowMap.enabled].join(); }
  private followLighting() {
    const before = this.lightingKey();
    this.channels.setLights(this.orbLights()); this.torches.setLimit(graphicsOptions.torchLightLimit);
    if (this.renderer.shadowMap.enabled !== graphicsOptions.shadows) { this.renderer.shadowMap.enabled = graphicsOptions.shadows; this.renderer.shadowMap.needsUpdate = true; }
    const after = this.lightingKey();
    if (this.disposed || after === before || (this.prepared && this.warmedLighting.has(after))) return;
    if (!this.prepared) { this.lightingChanged = true; return; }
    this.prepared = false; this.renderer.setAnimationLoop(null);
    const handlers = this.lightingHandlers;
    void this.prepare(this.preparedLobby, (message, fraction) => handlers?.progress(message, fraction))
      .catch(error => console.warn('The scene could not relink its shaders for the new lighting.', error))
      .finally(() => handlers?.done());
  }
  private contextLost = (event: Event) => {
    event.preventDefault();
    if (this.disposed) return;
    this.prepared = false; this.renderer.setAnimationLoop(null);
    this.contextHandlers?.lost();
  };
  private contextRestored = () => {
    if (this.disposed) return;
    // The picture filter's targets, queries and extensions belong to the old context; it is built again on its next frame.
    this.pictureFilter?.dispose(); this.pictureFilter = null; this.overlayWarmed = false; this.uploaded = new WeakSet(); this.warmedLighting.clear();
    const rebuilt = this.prepare(false, (message, fraction) => this.contextHandlers?.progress(message, fraction));
    this.contextHandlers?.restored(rebuilt);
    rebuilt.catch(error => console.warn('The scene could not be rebuilt after the WebGL context returned.', error));
  };
  private overlay: HudOverlay | null = null;
  /** Where a unit's figure is on screen, `height` metres above its feet, in CSS pixels from the canvas's top left; null when hidden or behind the camera. */
  screenPoint(id: string, height = 1): { x: number; y: number } | null {
    const group = this.units.get(id);
    if (!group?.visible) return null;
    const point = this.labelProjection.copy(group.position); point.y += height; point.project(this.camera);
    if (point.z > 1) return null;
    return { x: (point.x + 1) / 2 * this.viewport.width, y: (1 - point.y) / 2 * this.viewport.height };
  }
  /** Where a ground point is on screen, `height` metres above the ground, in CSS pixels from the canvas's top left; null behind the camera. */
  groundScreenPoint(at: Point, height = 0): { x: number; y: number } | null {
    const point = this.labelProjection.set(at.x, this.groundHeight(at.x, at.z) + height, at.z).project(this.camera);
    if (point.z > 1) return null;
    return { x: (point.x + 1) / 2 * this.viewport.width, y: (1 - point.y) / 2 * this.viewport.height };
  }
  /** A player pointed something out: ring it on the ground and on the chart. */
  showPing(ping: PingMark) { this.pings.add(ping); playPing(ping.kind); }
  /** Where a sound at a world point is heard from: quieter the further it is from the camera's focus, and panned to its side of the screen. */
  soundPlacement(at: { x: number; z: number }): VoicePlacement {
    const focus = this.cameraFocus();
    return placeSound(Math.hypot(at.x - focus.x, at.z - focus.z), this.labelProjection.set(at.x, this.groundHeight(at.x, at.z), at.z).project(this.camera).x);
  }
  private createCombatSounds(enemies: EnemyDefinition[]) {
    const sounds = new CombatSounds(enemies); sounds.place = at => this.soundPlacement(at); return sounds;
  }
  voicePlacement(id: string): VoicePlacement | null {
    const group = this.units.get(id);
    if (!group?.visible) return null;
    const focus = this.cameraFocus();
    return placeVoice(Math.hypot(group.position.x - focus.x, group.position.z - focus.z), group.position.clone().project(this.camera).x);
  }
  /** Development tools: where the camera looks and how far it is zoomed. */
  cameraView() { return { ...this.cameraFocus(), zoom: this.camera.zoom }; }
  /** Under a roof the point names its level: the cave below the ceiling, the rock's top above it. */
  private moveTarget(ground: THREE.Vector3): Point & { level?: number } {
    return { x: ground.x, z: ground.z, ...this.caves?.under(ground.x, ground.z) ? { level: this.caves.levelAt(ground.x, ground.z, ground.y) } : {} };
  }
  /** Where each selected hero would stand, and the way it would face, if the Ctrl-drag in progress ended now, in the party's formation. */
  private orientPreview(heroes: readonly UnitState[]): Map<string, { place: Point; facing: number }> {
    const orienting = this.orienting, preview = new Map<string, { place: Point; facing: number }>();
    if (!orienting) return preview;
    // In the party column's order, as the order is sent: that order fills the slots. The hand drawn goes out with the order, so the server
    // lays out exactly these slots; it flips only when the other spares the group clearly more walking, so the preview does not flicker.
    const going = this.selectedHeroes.flatMap(id => heroes.find(hero => hero.id === id && !hero.health.dead) ?? []);
    const order = { shape: this.formationShape, ...orienting.facing === undefined ? {} : { facing: orienting.facing } };
    let plan = planFormation(order, going, orienting.anchor);
    if (orienting.facing !== undefined && orienting.hand && plan.hand !== orienting.hand) {
      const kept = planFormation({ ...order, hand: orienting.hand }, going, orienting.anchor);
      if (plan.travel > kept.travel * .8) plan = kept;
    }
    if (orienting.facing !== undefined) orienting.hand = plan.hand;
    going.forEach((hero, index) => preview.set(hero.id, { place: plan.places[index], facing: plan.facing }));
    return preview;
  }
  /** The ground the ray meets as it is drawn: while a roof is cut away, the cave's floor under it and the surface elsewhere. */
  private pickGround() {
    const caves = this.caves, floors = this.mapVisual.caveGrounds;
    if (!caves || !floors?.length || caves.cut <= .5) return this.characterOcclusion.groundPoint(this.raycaster);
    const under = (point: THREE.Vector3) => caves.under(point.x, point.z) > 0;
    const hits = [this.characterOcclusion.groundPoint(this.raycaster, point => !under(point)),
      ...floors.map(floor => pickTerrainGround(floor, this.raycaster, point => under(point) && !this.characterOcclusion.terrainHidden(point)))];
    const origin = this.raycaster.ray.origin;
    return hits.filter((hit): hit is THREE.Vector3 => !!hit).sort((a, b) => a.distanceToSquared(origin) - b.distanceToSquared(origin))[0] ?? null;
  }
  /** The ground drawn under a point for anything that stands on no level of its own: under a cut roof, the cave's floor. */
  private groundHeight(x: number, z: number) { return this.caves ? this.caves.groundHeight(x, z) : terrainHeight(this.content.map, x, z); }
  /** Development tools: the ground under a screen point (client pixels), or null off the map. */
  groundAt(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    this.raycaster.setFromCamera(new THREE.Vector2((clientX - rect.left) / rect.width * 2 - 1, -(clientY - rect.top) / rect.height * 2 + 1), this.camera);
    const point = this.content.map.terrain ? this.pickGround()
      : this.raycaster.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), new THREE.Vector3());
    return point && Math.abs(point.x) <= this.content.map.width / 2 && Math.abs(point.z) <= this.content.map.depth / 2 ? point : null;
  }
  /** Development tools: a PNG data URL of the scene as just drawn, without plates, labels or panels (those are page elements). */
  snapshotImage(): string { this.render(); return this.renderer.domElement.toDataURL('image/png'); }
  /** Centres the camera on a unit where it stands now, even when it is off screen (and so culled); nothing if it is not in the snapshot. */
  focusUnit(id: string) {
    const unit = this.units.get(id);
    if (unit && (this.latest?.heroes.some(hero => hero.id === id) || this.latest?.enemies.some(enemy => enemy.id === id))) this.focusCamera({ x: unit.position.x, z: unit.position.z });
  }
  private focusParty() {
    const ids = this.selectedHeroes.length ? this.selectedHeroes : this.ownHeroes;
    const states = ids.map(id => this.latest?.heroes.find(hero => hero.id === id) ?? this.content.heroes.find(hero => hero.id === id)?.spawn).filter((point): point is Point => !!point);
    if (states.length) this.focusCamera({ x: states.reduce((sum, point) => sum + point.x, 0) / states.length, z: states.reduce((sum, point) => sum + point.z, 0) / states.length });
  }
  private cameraGroundView(): Point[] {
    this.camera.updateMatrixWorld(true);
    return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([x, y]) => {
      this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);
      const point = this.raycaster.ray.at((this.cameraGroundHeight - this.raycaster.ray.origin.y) / this.raycaster.ray.direction.y, new THREE.Vector3());
      return { x: point.x, z: point.z };
    });
  }
}

type ScreenBox = { left: number; right: number; top: number; bottom: number };
type HudOverlay = (renderer: THREE.WebGLRenderer, width: number, height: number, inside: boolean) => void;
/**
 * A CSS clip path that keeps everything but `blocks`, or '' for none. The boxes are cut into strips that do not overlap (overlapping
 * holes would cancel out under even-odd), and the kept area reaches well past the screen so plates at its edges are not cut.
 */
export function hudClipPath(blocks: readonly ScreenBox[], width: number, height: number) {
  const boxes = blocks.map(box => ({ left: Math.max(0, box.left), right: Math.min(width, box.right), top: Math.max(0, box.top), bottom: Math.min(height, box.bottom) }))
    .filter(box => box.right > box.left && box.bottom > box.top);
  if (!boxes.length) return '';
  const xs = [...new Set(boxes.flatMap(box => [box.left, box.right]))].sort((a, b) => a - b), round = (value: number) => Math.round(value * 10) / 10;
  let holes = '';
  for (let i = 0; i < xs.length - 1; i++) {
    const left = xs[i], right = xs[i + 1];
    const spans = boxes.filter(box => box.left <= left && box.right >= right).map(box => [box.top, box.bottom]).sort((a, b) => a[0] - b[0]);
    const merged: number[][] = [];
    for (const span of spans) { const last = merged.at(-1); if (last && span[0] <= last[1]) last[1] = Math.max(last[1], span[1]); else merged.push([...span]); }
    for (const [top, bottom] of merged) holes += ` M${round(left)} ${round(top)}H${round(right)}V${round(bottom)}H${round(left)}Z`;
  }
  const reach = Math.max(width, height);
  return holes ? `path(evenodd, 'M${-reach} ${-reach}H${width + reach}V${height + reach}H${-reach}Z${holes}')` : '';
}
